import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail, uploadDocument } from "@/lib/api";
import { GenerateBolDialog, SoBolCell, SoCarrierCell, SoShipmentStatusCell } from "@/components/GenerateBolDialog";
import { GenerateInvoiceDialog, SoInvoiceCell } from "@/components/InvoiceDialog";
import { ProduceConfirmDialog } from "@/components/ProduceConfirmDialog";
import { SoAddStockDialog } from "@/components/SoAddStockDialog";
import { SoLoadPhotosCell } from "@/components/SoLoadPhotos";
import { printSalesOrdersTable } from "@/lib/salesOrdersTablePrint";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Plus, Search, Pencil, Trash2, ShoppingCart, Loader2, Printer,
  Paperclip, X, MoreHorizontal, AlertTriangle, ArrowUpDown, CheckCircle2, RotateCcw, Truck, Ban, Clock, Sparkles,
  ZoomIn, ZoomOut, Download, Copy, FileText,
} from "lucide-react";
import * as XLSX from "xlsx-js-style";
import { printSalesOrder } from "@/lib/salesOrderPrint";
import { printProductionOrder } from "@/lib/productionOrderPrint";
import { buildOrderBOM, buildAvailability } from "@/lib/orderBom";
import { moNum } from "@/lib/moNum";
import { DODialog, DO_STATUS_META, doStatusMeta } from "@/pages/DeliveryOrders";
import { Link } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const SO_DOC_ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.png,.jpg,.jpeg";

function SoPoAttach({ so, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [parsing, setParsing] = useState(false);
  const atts = so.attachments || [];
  const runParse = async (path) => {
    setParsing(true);
    try {
      const { data } = await api.post(`/sales-orders/${so.id}/parse-po`, { path });
      const keys = Object.keys(data.applied || {});
      if (data.warning) toast.warning(data.warning, { duration: 12000 });
      if (keys.length) {
        toast.success(`Auto-filled from PO — ${keys.map((k) => `${k}: ${data.applied[k]}`).join(" · ")}`, { duration: 10000 });
        onChanged();
      } else {
        toast.info(data.message || "Couldn't detect fillable fields on this PO — enter them manually");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't auto-read the PO — fill fields manually");
    } finally { setParsing(false); }
  };
  const upload = async (f) => {
    setBusy(true);
    let uploaded = null;
    try {
      const d = await uploadDocument(f);
      uploaded = d;
      await api.post(`/sales-orders/${so.id}/attachments`, { name: d.name || f.name, url: d.url, path: d.path });
      toast.success(`${f.name} attached to ${so.so_number}`);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
      setBusy(false);
      return;
    }
    setBusy(false);
    await runParse(uploaded.path);
  };
  const fileInput = (
    <input type="file" className="hidden" accept={SO_DOC_ACCEPT}
      onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) upload(f); }} />
  );
  const [dragOver, setDragOver] = useState(false);
  const dropProps = {
    onDragOver: (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); },
    onDragEnter: (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); },
    onDragLeave: (e) => { e.preventDefault(); setDragOver(false); },
    onDrop: (e) => {
      e.preventDefault(); e.stopPropagation(); setDragOver(false);
      const f = e.dataTransfer?.files?.[0];
      if (f) upload(f);
      else toast.error("Drop a file — dragging directly from some email previews isn't supported; save the attachment first");
    },
  };
  const SQ = "flex h-7 w-7 shrink-0 items-center justify-center rounded-sm border transition-colors";
  if (!atts.length) {
    return (
      <label onClick={(e) => e.stopPropagation()} {...dropProps} title={parsing ? "Reading PO…" : "Upload or drag the Customer PO here — details will be auto-filled from it"}
        className={`${SQ} cursor-pointer ${dragOver ? "border-dashed border-[#002FA7] bg-blue-50 text-[#002FA7]" : "border-dashed border-slate-300 text-slate-400 hover:border-[#002FA7] hover:text-[#002FA7]"}`}
        data-testid={`so-po-upload-${so.id}`}>
        {busy || parsing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
        {fileInput}
      </label>
    );
  }
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button onClick={(e) => e.stopPropagation()} {...dropProps}
          title={parsing ? "Reading PO…" : `PO file${atts.length > 1 ? `s — ${atts.length}` : ""}`}
          className={`${SQ} relative ${dragOver ? "border-dashed border-[#002FA7] bg-blue-50 text-[#002FA7]" : "border-emerald-300 bg-emerald-50 text-emerald-700 hover:border-emerald-500"}`}
          data-testid={`so-po-files-${so.id}`}>
          {busy || parsing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
          {atts.length > 1 && <span className="absolute -right-1 -top-1 rounded-full bg-emerald-600 px-1 text-[9px] font-bold leading-3 text-white">{atts.length}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 rounded-sm p-3" onClick={(e) => e.stopPropagation()}>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Customer PO Documents</p>
        {atts.map((a, i) => (
          <div key={`${a.path}-${i}`} className="flex items-center justify-between gap-2 py-1 text-sm">
            <a href={a.url} target="_blank" rel="noreferrer" className="truncate text-[#002FA7] hover:underline" data-testid={`so-po-file-${so.id}-${i}`}>{a.name}</a>
            <button onClick={async () => { try { await api.delete(`/sales-orders/${so.id}/attachments/${i}`); onChanged(); } catch { toast.error("Failed to remove"); } }}
              className="shrink-0 text-slate-400 hover:text-red-600" data-testid={`so-po-file-remove-${so.id}-${i}`} title="Remove"><X className="h-3.5 w-3.5" /></button>
          </div>
        ))}
        {atts.some((a) => a.path) && (
          <Button size="sm" variant="outline" disabled={parsing}
            onClick={() => runParse(atts.filter((a) => a.path).slice(-1)[0].path)}
            className="mt-2 w-full rounded-sm text-xs" data-testid={`so-po-autofill-${so.id}`}>
            {parsing ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5 text-amber-500" />}
            {parsing ? "Reading PO…" : "Auto-fill fields from this PO"}
          </Button>
        )}
        <label {...dropProps}
          className={`mt-2 inline-flex cursor-pointer items-center gap-1.5 rounded-sm border border-dashed px-2 py-1 text-xs font-semibold ${dragOver ? "border-[#002FA7] bg-blue-50 text-[#002FA7]" : "border-slate-300 text-slate-500 hover:border-[#002FA7] hover:text-[#002FA7]"}`}
          data-testid={`so-po-upload-more-${so.id}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} {dragOver ? "Drop PO here" : "Upload or drag another"}
          {fileInput}
        </label>
      </PopoverContent>
    </Popover>
  );
}

function SoPickupDelivery({ so, onChanged }) {
  const [busy, setBusy] = useState(false);
  const val = so.pickup_delivery || "";
  const set = async (v) => {
    if (v === val) return;
    setBusy(true);
    try {
      await api.put(`/sales-orders/${so.id}/pickup-delivery`, { pickup_delivery: v });
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to update");
    } finally { setBusy(false); }
  };
  return (
    <Select value={val || undefined} onValueChange={set} disabled={busy}>
      <SelectTrigger className={`h-7 w-[110px] rounded-sm border-slate-200 px-2 text-xs ${val ? "font-semibold text-slate-800" : "text-slate-400"}`} data-testid={`so-pickup-delivery-${so.id}`}>
        <SelectValue placeholder="Select…" />
      </SelectTrigger>
      <SelectContent className="rounded-sm">
        <SelectItem value="pickup" data-testid={`so-pd-pickup-${so.id}`}>Pickup</SelectItem>
        <SelectItem value="delivery" data-testid={`so-pd-delivery-${so.id}`}>Delivery</SelectItem>
      </SelectContent>
    </Select>
  );
}

function SoShipFrom({ so, options, onChanged }) {
  const [busy, setBusy] = useState(false);
  const val = so.ship_from || "";
  const set = async (v) => {
    const next = v === "__clear__" ? "" : v;
    if (next === val) return;
    setBusy(true);
    try {
      await api.put(`/sales-orders/${so.id}/ship-from`, { ship_from: next });
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to update ship-from");
    } finally { setBusy(false); }
  };
  const groups = ["Plant", "Co-Packers", "Distributors"].map((g) => [g, options.filter((o) => o.group === g)]).filter(([, l]) => l.length);
  const known = options.some((o) => o.name === val);
  const address = options.find((o) => o.name === val)?.address || "";
  return (
    <div className="max-w-[160px]">
    <Select key={val || "empty"} value={val || undefined} onValueChange={set} disabled={busy}>
      <SelectTrigger className={`h-7 w-[130px] rounded-sm border-slate-200 px-2 text-xs ${val ? "font-semibold text-slate-800" : "text-slate-400"}`} data-testid={`so-ship-from-${so.id}`}>
        <SelectValue placeholder="Select…" />
      </SelectTrigger>
      <SelectContent className="rounded-sm">
        {val && !known && <SelectItem value={val}>{val}</SelectItem>}
        {groups.map(([g, list]) => (
          <SelectGroup key={g}>
            <SelectLabel className="text-[10px] uppercase tracking-wide text-slate-400">{g}</SelectLabel>
            {list.map((o) => (<SelectItem key={o.name} value={o.name} data-testid={`so-sf-${so.id}-${o.name.replace(/\W+/g, "-").toLowerCase()}`}>{o.name}</SelectItem>))}
          </SelectGroup>
        ))}
        {val && <SelectItem value="__clear__" className="text-slate-400">Clear selection</SelectItem>}
      </SelectContent>
    </Select>
    {val && address && (
      <p className="mt-1 text-[11px] leading-snug text-slate-500" data-testid={`so-ship-from-address-${so.id}`}>{address}</p>
    )}
    </div>
  );
}

function SoOrderCell({ so, products, onChanged }) {
  const single = so.lines.length === 1;
  const l0 = so.lines[0];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} className="group text-left" title="View items in this order" data-testid={`so-order-${so.id}`}>
          {single ? (
            <>
              <div className="font-medium text-slate-800 underline decoration-dotted decoration-slate-300 underline-offset-2 group-hover:text-[#002FA7]">{l0?.product_name}</div>
              <div className="text-xs text-slate-500">{l0?.design_name ? `${l0.design_name} · ` : ""}{Math.round(l0?.case_pack || 0)}-Pack</div>
            </>
          ) : (
            <div className="font-medium text-slate-800 underline decoration-dotted decoration-slate-300 underline-offset-2 group-hover:text-[#002FA7]">{so.lines.length} Products</div>
          )}
          <div className="mt-0.5"><OrderedCell so={so} products={products} /></div>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto max-w-[min(700px,92vw)] overflow-x-auto rounded-sm p-0" data-testid={`so-order-popover-${so.id}`}>
        <SOLinesTable so={so} products={products} compact onChanged={onChanged} />
      </PopoverContent>
    </Popover>
  );
}

function MobileField({ label, full = false, children }) {
  return (
    <div className={`min-w-0 ${full ? "col-span-2" : ""}`}>
      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      {children}
    </div>
  );
}

const PO_STATUS_META = {
  pending_review: { label: "Pending Review", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  approved: { label: "Approved", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

function SoPoStatusBadge({ so, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const status = so.po_status || "pending_review";
  const meta = PO_STATUS_META[status] || PO_STATUS_META.pending_review;
  const set = async (s) => {
    setBusy(true);
    try {
      await api.put(`/sales-orders/${so.id}/po-status`, { po_status: s });
      toast.success(s === "approved" ? `Customer PO on ${so.so_number} approved` : `Customer PO on ${so.so_number} set back to Pending Review`);
      setOpen(false);
      onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to update PO status");
    } finally { setBusy(false); }
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button onClick={(e) => e.stopPropagation()} title={`Customer PO: ${meta.label} — click to change`}
          className="inline-flex items-center rounded-sm p-0.5 transition-colors hover:bg-slate-100"
          data-testid={`so-po-status-${so.id}`}>
          {status === "approved"
            ? <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            : <Clock className="h-4 w-4 text-amber-500" />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-60 rounded-sm p-3" onClick={(e) => e.stopPropagation()}>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Customer PO Status</p>
        {status === "pending_review" ? (
          <Button size="sm" onClick={() => set("approved")} disabled={busy}
            className="w-full rounded-sm bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`so-po-approve-${so.id}`}>
            {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />} Mark Approved
          </Button>
        ) : (
          <Button size="sm" variant="outline" onClick={() => set("pending_review")} disabled={busy}
            className="w-full rounded-sm" data-testid={`so-po-unapprove-${so.id}`}>
            {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="mr-1.5 h-3.5 w-3.5" />} Revert to Pending Review
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}

const num = (n) => Math.round(n).toLocaleString();
const round2 = (n) => (Math.round(n * 100) / 100).toLocaleString();
const uid = () => Math.random().toString(36).slice(2, 10);
const today = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, n) => { const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const fmtShort = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—");
const fmtLong = (iso) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");

const SO_STATUS_META = {
  open: { label: "Open", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  shipped: { label: "Shipped", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancelled: { label: "Cancelled", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

const PAPERWORK_OPTIONS = [
  ["all", "Paperwork: All"], ["missing_any", "Missing any paperwork"], ["missing_signed_bol", "Missing signed BOL"],
  ["missing_pod", "Missing POD"], ["missing_invoice", "Missing invoice"],
];

const missingPaperwork = (so, key) => {
  const noSigned = !(so.signed_bol_docs || []).length;
  const noPod = !(so.pod_docs || []).length;
  const noInv = !(so.invoices || []).length && !(so.invoice_docs || []).length;
  if (key === "missing_signed_bol") return noSigned;
  if (key === "missing_pod") return noPod;
  if (key === "missing_invoice") return noInv;
  return noSigned || noPod || noInv;
};

const RANGE_OPTIONS = [
  ["all", "All dates"], ["this_week", "This week"], ["this_month", "This month"],
  ["last_30", "Last 30 days"], ["last_90", "Last 90 days"], ["this_year", "This year"],
];

const rangeStart = (key) => {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (key === "this_week") d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  else if (key === "this_month") d.setDate(1);
  else if (key === "last_30") d.setDate(d.getDate() - 30);
  else if (key === "last_90") d.setDate(d.getDate() - 90);
  else if (key === "this_year") { d.setMonth(0); d.setDate(1); }
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const poDateOf = (so) => so.order_date || (so.created_at || "").slice(0, 10) || "";

const PRIORITY_META = {
  1: { label: "1 — Critical", short: "1 Critical", cls: "bg-red-100 text-red-700 border-red-300" },
  2: { label: "2 — High", short: "2 High", cls: "bg-orange-100 text-orange-700 border-orange-300" },
  3: { label: "3 — Normal", short: "3 Normal", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  4: { label: "4 — Low", short: "4 Low", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

const OPS_META = {
  past_due: { label: "Past Due", cls: "bg-red-100 text-red-700 border-red-300" },
  waiting_bottles: { label: "Waiting on Bottles", cls: "bg-red-50 text-red-700 border-red-200" },
  waiting_components: { label: "Waiting on Components", cls: "bg-red-50 text-red-700 border-red-200" },
  production_required: { label: "Production Required", cls: "bg-orange-100 text-orange-700 border-orange-300" },
  at_risk: { label: "At Risk", cls: "bg-red-100 text-red-700 border-red-300" },
  in_production: { label: "In Production", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  producing_week: { label: "Producing This Week", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  scheduled: { label: "Production Scheduled", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  partially_ready: { label: "Partially Ready", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  ready: { label: "Ready to Ship", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  shipped: { label: "Shipped", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancelled: { label: "Cancelled", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};

// ---- Live component readiness: recompute a placed MO's availability against current inventory ----
function withLiveAvailability(mo, compIndex, byKey) {
  if (!mo.placed || !(mo.bom_snapshot || []).length) return mo;
  const ordered = mo.ordered_bottles || 0;
  const produced = mo.produced_bottles || 0;
  const frac = ordered > 0 ? Math.max(0, (ordered - produced) / ordered) : 1;
  const bom = frac === 1 ? mo.bom_snapshot : mo.bom_snapshot.map((b) => ({ ...b, required: Math.round((b.required || 0) * frac * 100) / 100 }));
  // Exclude this MO's own reservations so it isn't counted as short against itself
  const adjusted = {};
  Object.entries(byKey).forEach(([k, v]) => {
    const own = (v.orders || []).filter((o) => o.mwo_number === mo.mwo_number).reduce((s, o) => s + (o.reserved || 0), 0);
    adjusted[k] = { ...v, reserved: Math.max(0, (v.reserved || 0) - own), orders: (v.orders || []).filter((o) => o.mwo_number !== mo.mwo_number) };
  });
  try {
    return { ...mo, availability: buildAvailability(bom, compIndex, adjusted), availability_live: true };
  } catch {
    return mo;
  }
}

// ---- Fulfillment / operational status derivation ----
function lineFul(l, moMap) {
  const ordered = l.cases || 0;
  const allocated = l.fg_reserved_cases || 0;
  const remaining = l.remaining_cases != null ? l.remaining_cases : Math.max(0, ordered - allocated);
  const available = l.available_cases || 0;
  const stock = allocated;
  const rawShort = l.shortage_cases || 0;
  const mo = l.mo_id ? moMap[l.mo_id] : null;
  const moCases = mo && l.case_pack ? (mo.bottle_quantity || 0) / l.case_pack : 0;
  const inProd = mo ? Math.min(rawShort, moCases) : 0;
  const waived = l.no_auto_mo ? Math.max(0, rawShort - inProd) : 0;
  const shortage = rawShort - waived;
  const uncovered = Math.max(0, shortage - inProd);
  return { ordered, stock, allocated, remaining, available, shortage, waived, inProd, uncovered, mo };
}

// One-click: fill every line of the order from free stock.
function AllocateAllButton({ so, onChanged }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sales-orders/${so.id}/allocate-all`);
      toast.success(`${num(data.allocated_now)} cases allocated to ${so.so_number}.`);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); run(); }} disabled={busy}
      className="whitespace-nowrap rounded-sm bg-[#002FA7] px-2 py-1 text-[10px] font-semibold text-white transition-colors hover:bg-[#002080] disabled:opacity-50"
      title="Fill every line of this order from free stock"
      data-testid={`so-allocate-all-${so.id}`}>{busy ? "…" : "Allocate All"}</button>
  );
}

// Inline allocate / release control for one SO line (manual stock allocation).
function AllocateControl({ so, line, f, onChanged, testId }) {
  const [open, setOpen] = useState(false);
  const maxAlloc = Math.max(0, Math.min(f.remaining, f.available));
  const [qty, setQty] = useState("");
  const [busy, setBusy] = useState(false);
  const apply = async (target, verb) => {
    setBusy(true);
    try {
      await api.post(`/sales-orders/${so.id}/allocate`, { line_id: line.id, cases: Math.max(0, target) });
      toast.success(`${verb} — ${line.product_name}${line.design_name ? ` ${line.design_name}` : ""}.`);
      setOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };
  if (f.remaining <= 0.01 && f.allocated <= 0.01) return null;
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setQty(String(Math.round(maxAlloc * 100) / 100)); }}>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()}
          className={`inline-flex items-center whitespace-nowrap rounded-sm border px-1.5 py-0.5 text-[10px] font-semibold transition-colors ${f.available > 0.01 && f.remaining > 0.01 ? "border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/5" : "border-slate-200 text-slate-500 hover:border-slate-400"}`}
          title="Allocate free stock to this order line, or release it back"
          data-testid={testId}>Allocate</button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 rounded-sm p-3" onClick={(e) => e.stopPropagation()} data-testid={`${testId}-panel`}>
        <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Allocate Stock</p>
        <div className="mb-2 text-[11px] tabular-nums leading-tight text-slate-500">
          <p>Allocated: <span className="font-semibold text-[#002FA7]">{num(f.allocated)}</span> · Remaining: <span className="font-semibold text-slate-700">{num(f.remaining)}</span></p>
          <p>Available free stock: <span className="font-semibold text-emerald-700">{num(f.available)}</span></p>
        </div>
        <Input type="text" inputMode="numeric" value={qty} onChange={(e) => setQty(e.target.value.replace(/[^0-9.]/g, ""))}
          className="h-8 rounded-sm text-right tabular-nums" data-testid={`${testId}-qty`} />
        <div className="mt-2 flex gap-1.5">
          <Button size="sm" disabled={busy || !(Number(qty) > 0) || f.remaining <= 0.01}
            onClick={() => apply(f.allocated + (Number(qty) || 0), `${num(Number(qty))} cases allocated`)}
            className="h-8 flex-1 rounded-sm bg-[#002FA7] text-xs text-white hover:bg-[#002080]" data-testid={`${testId}-confirm`}>
            {busy ? "…" : "Allocate"}
          </Button>
          {f.allocated > 0.01 && (
            <Button size="sm" variant="outline" disabled={busy || !(Number(qty) > 0)}
              onClick={() => apply(f.allocated - (Number(qty) || 0), `${num(Math.min(Number(qty), f.allocated))} cases released back to free stock`)}
              className="h-8 flex-1 rounded-sm border-slate-300 text-xs text-slate-600 hover:border-red-400 hover:text-red-600" data-testid={`${testId}-release`}>
              Release
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function deriveOps(so, moMap) {
  const meta = (key, risk, reasons) => ({ key, risk, reasons, ...OPS_META[key] });
  if (so.status === "cancelled") return meta("cancelled", false, []);
  if (so.status === "shipped") return meta("shipped", false, []);
  const t = today();
  const fs = (so.lines || []).map((l) => lineFul(l, moMap));
  const reasons = [];
  const uncovered = fs.reduce((s, f) => s + f.uncovered, 0);
  const shortage = fs.reduce((s, f) => s + f.shortage, 0);
  const pastDue = !!so.ship_date && so.ship_date < t;
  const mos = fs.map((f) => f.mo).filter(Boolean);
  let compShort = false, bottleShort = false;
  mos.forEach((mo) => {
    if (mo.status === "Completed" || mo.status === "Produced – Report Pending") return;
    (mo.availability || []).forEach((a) => {
      if ((a.shortage || 0) > 0) {
        compShort = true;
        if (/bottle|preform/i.test(`${a.name} ${a.type}`)) bottleShort = true;
        reasons.push(`${num(a.shortage)} ${a.type ? `${a.type} ` : ""}${a.name} short`);
      }
    });
  });
  const noMoShort = fs.some((f) => f.shortage > 0.01 && !f.mo);
  if (uncovered > 0.01) reasons.unshift(`${num(uncovered)} cases uncovered`);
  if (pastDue) reasons.unshift("Past due");
  const risk = pastDue || uncovered > 0.01 || compShort;
  // Readiness wins over lateness: fully covered orders are always "Ready to Ship",
  // pastDue keeps flagging them via risk/reasons and the date-based Past Due filter.
  if (shortage <= 0.01 && uncovered <= 0.01 && !noMoShort) return meta("ready", pastDue, pastDue ? ["Past due"] : []);
  if (pastDue) return meta("past_due", risk, reasons);
  if (bottleShort) return meta("waiting_bottles", risk, reasons);
  if (compShort) return meta("waiting_components", risk, reasons);
  if (noMoShort) return meta("production_required", risk, reasons);
  if (uncovered > 0.01) return meta("at_risk", risk, reasons);
  if (shortage > 0.01) {
    if (mos.some((m) => m.status === "In Production" || m.status === "Partially Completed")) return meta("in_production", risk, reasons);
    if (mos.some((m) => m.production_date && m.production_date >= t && m.production_date <= addDays(t, 7))) return meta("producing_week", risk, reasons);
    if (mos.some((m) => m.placed)) return meta("scheduled", risk, reasons);
    return meta("partially_ready", risk, reasons);
  }
  return meta("ready", false, []);
}

const emptyLine = () => ({ _k: uid(), id: "", product_id: "", config_id: "", design_id: "", quantity: "", unit: "pallets" });

function LineEditor({ line, products, onChange, onRemove, canRemove, idx }) {
  const prod = products.find((p) => p.id === line.product_id) || null;
  const cfgs = prod?.pack_configs || [];
  const designs = useMemo(() => {
    const seen = new Set();
    const out = [];
    (prod?.lines || []).forEach((l) => (l.designs || []).forEach((d) => { if (d?.id && !seen.has(d.id)) { seen.add(d.id); out.push(d); } }));
    return out.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [prod]);
  const cfg = cfgs.find((c) => c.id === line.config_id) || cfgs[0] || null;
  const cpp = Number(cfg?.cases_per_pallet) || 0;
  const ppt = Number(cfg?.pallets_per_truckload) || 0;
  const qty = Number(line.quantity) || 0;
  const cases = line.unit === "truckloads" ? qty * ppt * cpp : line.unit === "pallets" ? qty * cpp : qty;
  return (
    <div data-testid={`so-line-${idx}`}>
      <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-[1.3fr_1fr_1fr_80px_100px_36px] sm:items-center">
        <Select value={line.product_id || undefined} onValueChange={(v) => { const p = products.find((x) => x.id === v); onChange({ ...line, product_id: v, config_id: p?.pack_configs?.[0]?.id || "", design_id: "" }); }}>
          <SelectTrigger className="rounded-sm" data-testid={`so-line-product-${idx}`}><SelectValue placeholder="Product" /></SelectTrigger>
          <SelectContent>{products.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}</SelectContent>
        </Select>
        {designs.length > 0 ? (
          <Select value={line.design_id || undefined} onValueChange={(v) => onChange({ ...line, design_id: v })}>
            <SelectTrigger className="rounded-sm" data-testid={`so-line-design-${idx}`}><SelectValue placeholder="Design" /></SelectTrigger>
            <SelectContent>{designs.map((d) => (<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>))}</SelectContent>
          </Select>
        ) : (
          <Input readOnly value="" placeholder="—" className="rounded-sm bg-slate-50 text-slate-400" />
        )}
        {cfgs.length > 1 ? (
          <Select value={cfg?.id || undefined} onValueChange={(v) => onChange({ ...line, config_id: v })}>
            <SelectTrigger className="rounded-sm" data-testid={`so-line-pack-${idx}`}><SelectValue placeholder="Pack" /></SelectTrigger>
            <SelectContent>{cfgs.map((c) => (<SelectItem key={c.id} value={c.id}>{c.label || `${c.bottles_per_case}-pack`}</SelectItem>))}</SelectContent>
          </Select>
        ) : (
          <Input readOnly value={cfg?.bottles_per_case ? `${Math.round(cfg.bottles_per_case)}-pack` : ""} placeholder="—" className="rounded-sm bg-slate-50 text-slate-600" />
        )}
        <Input type="text" inputMode="decimal" value={line.quantity} onChange={(e) => onChange({ ...line, quantity: e.target.value.replace(/[^0-9.]/g, "") })} placeholder="Qty" className="rounded-sm text-right tabular-nums" data-testid={`so-line-qty-${idx}`} />
        <Select value={line.unit} onValueChange={(v) => onChange({ ...line, unit: v })}>
          <SelectTrigger className="rounded-sm" data-testid={`so-line-unit-${idx}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="truckloads">Truckloads</SelectItem>
            <SelectItem value="pallets">Pallets</SelectItem>
            <SelectItem value="cases">Cases</SelectItem>
          </SelectContent>
        </Select>
        <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={onRemove} disabled={!canRemove} data-testid={`so-line-remove-${idx}`}><Trash2 className="h-4 w-4" /></Button>
      </div>
      {cases > 0 && <p className="mt-0.5 pl-1 text-[10px] text-slate-400">= {num(cases)} cases{cpp && line.unit !== "pallets" ? ` (${(Math.round((cases / cpp) * 100) / 100).toLocaleString()} pallets)` : ""}</p>}
      {line.unit === "truckloads" && qty > 0 && (!ppt || !cpp) && <p className="mt-0.5 pl-1 text-[10px] text-amber-600">This pack config needs cases/pallet and pallets/truckload set to use truckloads.</p>}
    </div>
  );
}

export function SODialog({ open, onOpenChange, initial, customers, products, onSaved }) {
  const [customerId, setCustomerId] = useState("");
  const [customerPo, setCustomerPo] = useState("");
  const [orderDate, setOrderDate] = useState(today());
  const [shipDate, setShipDate] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCustomerId(initial?.customer_id || "");
    setCustomerPo(initial?.customer_po || "");
    setOrderDate(initial?.order_date || today());
    setShipDate(initial?.ship_date || "");
    setNotes(initial?.notes || "");
    setAttachments(initial?.attachments || []);
    setLines(initial?.lines?.length
      ? initial.lines.map((l) => ({ _k: uid(), id: l.id, product_id: l.product_id, config_id: l.config_id, design_id: l.design_id || "", quantity: String(l.quantity), unit: l.unit || "cases" }))
      : [emptyLine()]);
  }, [open, initial]);

  const valid = customerId && lines.every((l) => l.product_id && Number(l.quantity) > 0);

  const save = async () => {
    if (!valid) return;
    setSaving(true);
    try {
      const payload = {
        customer_id: customerId, customer_po: customerPo, order_date: orderDate, ship_date: shipDate, notes,
        attachments,
        status: initial?.status || "open",
        priority: initial?.priority || null,
        lines: lines.map((l) => ({ id: l.id || "", product_id: l.product_id, config_id: l.config_id || "", design_id: l.design_id || "", quantity: Number(l.quantity), unit: l.unit })),
      };
      let saved;
      if (initial?.id) {
        const { data } = await api.put(`/sales-orders/${initial.id}`, payload);
        saved = data;
        if (data.mo_dates_synced?.length) toast.info(`Linked MO date${data.mo_dates_synced.length > 1 ? "s" : ""} moved to ${payload.ship_date}: ${data.mo_dates_synced.join(", ")}`);
        (data.mo_actions || []).forEach((a) => toast.info(a.message));
        const linked = data.lines.filter((l) => l.mo_id);
        if (linked.length) toast.info(`${data.so_number} has linked MO(s) — review quantities on the Production Schedule if the order changed.`);
      } else {
        const { data } = await api.post("/sales-orders", payload);
        saved = data;
      }
      toast.success(initial?.id ? "Sales order updated" : "Sales order created — finished goods reserved");
      onOpenChange(false);
      onSaved(saved);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to save sales order");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-3xl" data-testid="so-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">{initial?.id ? `Edit ${initial.so_number}` : "New Sales Order"}</DialogTitle>
          <DialogDescription>Open sales orders reserve finished goods so the same pallet is never promised twice.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Customer</Label>
              <Select value={customerId || undefined} onValueChange={setCustomerId}>
                <SelectTrigger className="rounded-sm" data-testid="so-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>{customers.map((c) => (<SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Customer PO #</Label>
              <Input value={customerPo} onChange={(e) => setCustomerPo(e.target.value)} placeholder="Optional reference" className="rounded-sm" data-testid="so-customer-po" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Order Date</Label>
              <Input type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} className="rounded-sm" data-testid="so-order-date" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Deliver / Pickup Date</Label>
              <Input type="date" value={shipDate} onChange={(e) => setShipDate(e.target.value)} className="rounded-sm" data-testid="so-ship-date" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Line Items</Label>
            {lines.map((l, i) => (
              <LineEditor key={l._k} line={l} products={products} idx={i}
                onChange={(nl) => setLines((p) => p.map((x) => (x._k === l._k ? { ...nl, _k: l._k, id: l.id } : x)))}
                onRemove={() => setLines((p) => p.filter((x) => x._k !== l._k))}
                canRemove={lines.length > 1} />
            ))}
            <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={() => setLines((p) => [...p, emptyLine()])} data-testid="so-add-line">
              <Plus className="mr-1 h-3.5 w-3.5" /> Add line
            </Button>
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="rounded-sm" placeholder="Optional notes…" data-testid="so-notes" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">Customer PO Documents</Label>
            <div className="flex flex-wrap items-center gap-2">
              {attachments.map((a, i) => (
                <span key={`${a.path}-${i}`} className="inline-flex items-center gap-1.5 rounded-sm border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-700" data-testid={`so-attachment-${i}`}>
                  <Paperclip className="h-3 w-3 text-slate-400" />
                  <span className="max-w-[180px] truncate">{a.name}</span>
                  <button type="button" onClick={() => setAttachments((p) => p.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600" data-testid={`so-attachment-remove-${i}`}><X className="h-3 w-3" /></button>
                </span>
              ))}
              <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-sm border border-dashed border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-500 hover:border-[#002FA7] hover:text-[#002FA7]">
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />} Attach PO file
                <input type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" data-testid="so-attachment-input"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (!f) return;
                    setUploading(true);
                    try {
                      const d = await uploadDocument(f);
                      setAttachments((p) => [...p, { name: d.name || f.name, url: d.url, path: d.path }]);
                      toast.success(`${f.name} attached`);
                    } catch (err) {
                      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
                    } finally {
                      setUploading(false);
                    }
                  }} />
              </label>
            </div>
            <p className="text-[10px] text-slate-400">PDF, Word, Excel, CSV or TXT up to 25MB. Saved with the sales order.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button disabled={!valid || saving} onClick={save} className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="so-save">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{initial?.id ? "Save Changes" : "Create Sales Order"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---- Control-board pieces ----
function lineLogistics(l, products) {
  const pallets = l.cases_per_pallet ? l.cases / l.cases_per_pallet : null;
  const prodCfg = (products.find((p) => p.id === l.product_id)?.pack_configs || []).find((c) => c.id === l.config_id);
  const ppt = Number(l.pallets_per_truckload) || Number(prodCfg?.pallets_per_truckload) || 0;
  const trucks = pallets != null && ppt ? pallets / ppt : null;
  return { pallets, trucks };
}

function OrderedCell({ so, products }) {
  const calcs = so.lines.map((l) => lineLogistics(l, products));
  const cases = so.lines.reduce((s, l) => s + (l.cases || 0), 0);
  const bottles = so.lines.reduce((s, l) => s + (l.bottles || 0), 0);
  const pallets = calcs.some((c) => c.pallets != null) ? calcs.reduce((s, c) => s + (c.pallets || 0), 0) : null;
  const trucks = calcs.some((c) => c.trucks != null) ? calcs.reduce((s, c) => s + (c.trucks || 0), 0) : null;
  const top = [trucks != null ? `${round2(trucks)} Truckload${trucks === 1 ? "" : "s"}` : null, pallets != null ? `${round2(pallets)} Pallet${pallets === 1 ? "" : "s"}` : null].filter(Boolean).join(" · ");
  return (
    <div className="whitespace-nowrap text-slate-600" data-testid={`so-ordered-${so.id}`}>
      {top && <div className="text-xs text-slate-500">{top}</div>}
      <div>{num(cases)} Case{cases === 1 ? "" : "s"} · {num(bottles)} Bottles</div>
    </div>
  );
}

function FulDetailPopover({ badgeCls, text, title, rows, deco = "decoration-slate-300", testId, action }) {
  if (!rows.length) return <span className={badgeCls}>{text}</span>;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} className={`${badgeCls} underline decoration-dotted ${deco} underline-offset-2`} data-testid={testId}>{text}</button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 rounded-sm p-3" onClick={(e) => e.stopPropagation()}>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</p>
        {rows.map((r, i) => (
          <div key={r.l?.id || i} className="flex items-end justify-between gap-3 border-b border-slate-100 py-1.5 last:border-b-0" data-testid={`${testId}-row-${i}`}>
            <div className="min-w-0 text-xs leading-tight">
              <p className="truncate font-medium text-slate-700">{r.l.product_name}</p>
              <p className="truncate text-slate-500">{r.l.design_name ? `${r.l.design_name} · ` : ""}{Math.round(r.l.case_pack || 0)}pk</p>
            </div>
            <span className="whitespace-nowrap text-xs font-semibold tabular-nums text-slate-800">{num(r.v)} cases</span>
          </div>
        ))}
        {action && (
          <Button size="sm" className="mt-2.5 h-8 w-full rounded-sm bg-[#002FA7] text-xs hover:bg-[#00227a]"
            disabled={action.busy} onClick={action.onClick} data-testid={`${testId}-action`}>
            {action.busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}{action.busy ? "Scheduling…" : action.label}
          </Button>
        )}
      </PopoverContent>
    </Popover>
  );
}

function CreateMoDialog({ so, products, onClose, onCreate, busy }) {
  const items = useMemo(() => (so?.lines || []).map((l, i) => ({ l, short: so._fs?.[i]?.uncovered || 0 })).filter((x) => x.short > 0.01), [so]);
  const [qty, setQty] = useState({});
  useEffect(() => { setQty(Object.fromEntries(items.map(({ l, short }) => [l.id, String(Math.ceil(short))]))); }, [items]);
  const cppOf = (l) => {
    const p = products.find((x) => x.id === l.product_id);
    const cfg = (p?.pack_configs || []).find((c) => c.id === l.config_id) || p?.pack_configs?.[0];
    return Number(cfg?.cases_per_pallet) || 0;
  };
  const roundUp = (l) => {
    const cpp = cppOf(l);
    if (!cpp) return;
    setQty((q) => ({ ...q, [l.id]: String(Math.max(1, Math.ceil((Number(q[l.id]) || 0) / cpp)) * cpp) }));
  };
  return (
    <Dialog open={!!so} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-lg" data-testid="create-mo-dialog">
        <DialogHeader>
          <DialogTitle>Create Manufacturing Order{items.length > 1 ? "s" : ""} — {so?.so_number}</DialogTitle>
          <DialogDescription>Quantity is prefilled with the uncovered shortage. Adjust it, or round up to full pallets.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {items.map(({ l, short }) => {
            const cpp = cppOf(l);
            const cases = Number(qty[l.id]) || 0;
            return (
              <div key={l.id} className="rounded-sm border border-slate-200 p-3" data-testid={`create-mo-line-${l.id}`}>
                <p className="text-sm font-medium text-slate-800">{l.product_name}{l.design_name ? ` — ${l.design_name}` : ""}{l.case_pack ? ` — ${Math.round(l.case_pack)}pk` : ""}</p>
                <p className="text-xs text-slate-500">{num(short)} cases uncovered</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Input type="number" min="0" value={qty[l.id] ?? ""} onChange={(e) => setQty((q) => ({ ...q, [l.id]: e.target.value }))}
                    className="h-8 w-28 rounded-sm text-right tabular-nums" data-testid={`create-mo-qty-${l.id}`} />
                  <span className="text-xs text-slate-500">cases{cpp ? ` · ${num(Math.round((cases / cpp) * 100) / 100)} pallets` : ""}{l.case_pack ? ` · ${num(cases * l.case_pack)} bottles` : ""}</span>
                  {cpp > 0 && (
                    <button type="button" onClick={() => roundUp(l)}
                      className="rounded-sm border border-slate-300 px-2 py-0.5 text-[11px] font-semibold text-slate-600 hover:border-[#002FA7] hover:text-[#002FA7]"
                      data-testid={`create-mo-roundup-${l.id}`}>Round up to full pallets</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" className="rounded-sm" onClick={onClose} data-testid="create-mo-cancel">Cancel</Button>
          <Button className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" disabled={busy}
            onClick={() => onCreate(items.map(({ l }) => ({ l, cases: Number(qty[l.id]) || 0 })).filter((x) => x.cases > 0))}
            data-testid="create-mo-confirm">
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null} Create MO{items.length > 1 ? "s" : ""}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FulfillmentSummary({ so, onSchedule, scheduling, onProduce, onAddStock, onRecordProduction, onUnproduce, onChanged }) {
  const allocated = so._fs.reduce((s, f) => s + f.allocated, 0);
  const remaining = so._fs.reduce((s, f) => s + f.remaining, 0);
  const availByKey = {};
  so.lines.forEach((l, i) => { availByKey[l.key || i] = so._fs[i]?.available || 0; });
  const available = Object.values(availByKey).reduce((s, v) => s + v, 0);
  const readyToFulfill = so.status === "open" && so.lines.length > 0 && so._fs.every((f) => f.remaining <= 0.01);
  const inProd = so._fs.reduce((s, f) => s + f.inProd, 0);
  const shortage = so._fs.reduce((s, f) => s + f.shortage, 0);
  const uncovered = so._fs.reduce((s, f) => s + f.uncovered, 0);
  const waived = so._fs.reduce((s, f) => s + (f.waived || 0), 0);
  const mos = [...new Map(so._fs.filter((f) => f.mo).map((f) => [f.mo.id, f.mo])).values()];
  const moCases = {};
  so._fs.forEach((f) => { if (f.mo) moCases[f.mo.id] = (moCases[f.mo.id] || 0) + f.inProd; });
  const badge = "inline-flex items-center whitespace-nowrap rounded-full px-1.5 py-0.5 text-[10px] font-semibold";
  const lineRows = (field) => so.lines.map((l, i) => ({ l, v: so._fs[i]?.[field] || 0 })).filter((r) => r.v > 0.01);
  return (
    <div data-testid={`so-fulfillment-${so.id}`}>
      <div className="flex flex-wrap items-center gap-1">
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" onClick={(e) => e.stopPropagation()}
              className={`${badge} ${allocated > 0 ? "bg-[#002FA7]/10 text-[#002FA7]" : "bg-slate-100 text-slate-400"} underline decoration-dotted decoration-[#002FA7]/40 underline-offset-2`}
              title="Stock allocated to this order — click to allocate or release"
              data-testid={`so-stock-badge-${so.id}`}>{num(allocated)} Allocated</button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-[22rem] rounded-sm p-3" onClick={(e) => e.stopPropagation()} data-testid={`so-stock-popover-${so.id}`}>
            <div className="mb-2 flex items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Stock Allocation</p>
              {so.status === "open" && so._fs.some((f) => f.remaining > 0.01 && f.available > 0.01) && (
                <AllocateAllButton so={so} onChanged={onChanged} />
              )}
            </div>
            {(so.lines || []).map((l, i) => {
              const f = so._fs[i] || {};
              return (
                <div key={l.id || i} className="border-b border-slate-100 py-1.5 last:border-b-0" data-testid={`so-stock-row-${so.id}-${i}`}>
                  <p className="truncate text-xs font-medium text-slate-700">{l.product_name}{l.design_name ? ` — ${l.design_name}` : ""}{l.case_pack ? ` — ${Math.round(l.case_pack)}-Pack` : ""}</p>
                  <div className="mt-0.5 flex items-end justify-between gap-2">
                    <div className="text-[11px] tabular-nums leading-tight text-slate-500">
                      <p>Allocated: <span className={`font-semibold ${(f.allocated || 0) > 0 ? "text-[#002FA7]" : "text-slate-500"}`} data-testid={`so-line-allocated-${so.id}-${i}`}>{num(f.allocated || 0)}</span></p>
                      <p>Remaining: <span className={`font-semibold ${(f.remaining || 0) > 0 ? "text-slate-700" : "text-emerald-700"}`} data-testid={`so-line-remaining-${so.id}-${i}`}>{num(f.remaining || 0)}</span></p>
                      <p>Available: <span className={`font-semibold ${(f.available || 0) > 0 ? "text-emerald-700" : "text-slate-400"}`} data-testid={`so-line-available-${so.id}-${i}`}>{num(f.available || 0)}</span></p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {so.status === "open" && <AllocateControl so={so} line={l} f={f} onChanged={onChanged} testId={`so-allocate-${so.id}-${i}`} />}
                      {onAddStock && (
                        <button type="button" onClick={() => onAddStock({ so, line: l })}
                          className="whitespace-nowrap rounded-sm border border-emerald-600 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 transition-colors hover:bg-emerald-50"
                          data-testid={`so-add-stock-${so.id}-${i}`}>Add Stock</button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </PopoverContent>
        </Popover>
        {readyToFulfill && (
          <span className={`${badge} bg-emerald-100 text-emerald-800`} title="Every line is fully allocated — shipping will consume this stock" data-testid={`so-ready-fulfill-${so.id}`}>
            Ready to Fulfill
          </span>
        )}
        {!readyToFulfill && remaining > 0.01 && available > 0.01 && (
          <span className={`${badge} bg-emerald-50 text-emerald-700`} title="Free stock of these items — open Allocated to assign it" data-testid={`so-available-badge-${so.id}`}>
            {num(Math.min(available, remaining))} Available
          </span>
        )}
        {(inProd > 0 || shortage > 0 || mos.some((m) => m.status === "Produced – Report Pending")) && (
          mos.length > 0 ? (
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" onClick={(e) => e.stopPropagation()}
                  className={`${badge} ${inProd > 0 ? "bg-orange-50 text-orange-700" : mos.some((m) => m.status === "Produced – Report Pending") ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-400"} underline decoration-dotted decoration-orange-300 underline-offset-2`}
                  title="View the Manufacturing Orders covering this" data-testid={`so-production-badge-${so.id}`}>
                  {inProd > 0 ? `${num(inProd)} Production` : mos.some((m) => m.status === "Produced – Report Pending") ? "Report Pending" : `${num(inProd)} Production`}
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-72 rounded-sm p-3" onClick={(e) => e.stopPropagation()} data-testid={`so-mo-popover-${so.id}`}>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Manufacturing Orders</p>
                {mos.map((mo) => (
                  <div key={mo.id} className="border-b border-slate-100 py-1.5 last:border-b-0" data-testid={`so-mo-row-${so.id}-${mo.id}`}>
                    <div className="flex items-center justify-between gap-2 text-sm">
                      <Link to="/production-planning" className="font-semibold text-[#002FA7] hover:underline">{moNum(mo.mwo_number)}</Link>
                      <span className="flex items-center gap-1.5">
                        <button type="button" onClick={() => printProductionOrder(mo)} title="Production Order document"
                          className="p-0.5 text-slate-500 transition-colors hover:text-[#002FA7]"
                          data-testid={`so-mo-doc-${so.id}-${mo.id}`}>
                          <FileText className="h-3.5 w-3.5" />
                        </button>
                        <span className={`whitespace-nowrap rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${mo.status === "Completed" ? "bg-emerald-50 text-emerald-700" : mo.status === "Produced – Report Pending" ? "bg-blue-50 text-blue-700" : "bg-orange-50 text-orange-700"}`}>{mo.status || "Planned"}</span>
                        {mo.status === "Awaiting Production" && onProduce && (
                          <button type="button" onClick={() => onProduce(mo)}
                            className="whitespace-nowrap rounded-sm bg-[#002FA7] px-2 py-0.5 text-[10px] font-semibold text-white transition-colors hover:bg-[#00227a]"
                            data-testid={`so-produce-${mo.id}`}>Produce</button>
                        )}
                        {mo.status === "Produced – Report Pending" && (
                          <>
                            <Link to="/production-planning" className="whitespace-nowrap rounded-sm border border-[#002FA7] px-2 py-0.5 text-[10px] font-semibold text-[#002FA7] transition-colors hover:bg-[#002FA7]/5"
                              data-testid={`so-enter-report-${mo.id}`}>Enter Report</Link>
                            {onUnproduce && (
                              <button type="button" onClick={() => onUnproduce(mo)}
                                className="whitespace-nowrap rounded-sm border border-red-300 px-2 py-0.5 text-[10px] font-semibold text-red-600 transition-colors hover:bg-red-50"
                                title="Undo this production — restores components, removes the finished goods, MO returns to Awaiting Production"
                                data-testid={`so-unproduce-${mo.id}`}>Reverse</button>
                            )}
                          </>
                        )}
                      </span>
                    </div>
                    <div className="mt-0.5 flex items-end justify-between gap-3">
                      <div className="min-w-0 text-xs leading-tight">
                        <p className="truncate font-medium text-slate-700">{mo.product_name}</p>
                        <p className="truncate text-slate-500">{mo.design_name}{mo.case_pack ? ` · ${Math.round(mo.case_pack)}pk` : ""}</p>
                      </div>
                      <div className="whitespace-nowrap text-right text-xs tabular-nums leading-tight">
                        <p className="font-semibold text-slate-800">{num(moCases[mo.id] || 0)} cases</p>
                        {mo.bottle_quantity ? <p className="text-slate-400">{num(mo.bottle_quantity)} bottles on MO</p> : null}
                      </div>
                    </div>
                  </div>
                ))}
              </PopoverContent>
            </Popover>
          ) : (
            <span className={`${badge} ${inProd > 0 ? "bg-orange-50 text-orange-700" : "bg-slate-100 text-slate-400"}`}>{num(inProd)} Production</span>
          )
        )}
        {uncovered > 0.01 && (
          <FulDetailPopover badgeCls={`${badge} bg-red-100 text-red-700`} deco="decoration-red-300"
            text={`${num(uncovered)} Short`} title="Uncovered Shortage — Needs Action"
            rows={lineRows("uncovered")} testId={`so-short-badge-${so.id}`}
            action={onSchedule ? {
              label: "Create MO to cover shortage",
              onClick: onSchedule, busy: scheduling,
            } : null} />
        )}
        {waived > 0.01 && (
          <FulDetailPopover badgeCls={`${badge} bg-slate-100 text-slate-500`} deco="decoration-slate-300"
            text={`${num(waived)} Waived`} title="Waived Shortage — No Production Scheduled"
            rows={lineRows("waived")} testId={`so-waived-sum-${so.id}`} />
        )}
        {so.status === "open" && (() => {
          const shippedFor = (l) => (so._dv?.byLine?.[l.id] || so._dv?.byLine?.[`${l.product_id}|${l.design_id || ""}|${l.config_id || ""}`])?.shipped || 0;
          const unrecorded = (so.lines || []).filter((l) => !l.mo_id && shippedFor(l) > 0.01);
          if (!unrecorded.length) return null;
          return (
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" onClick={(e) => e.stopPropagation()}
                  className={`${badge} bg-amber-100 text-amber-800 underline decoration-dotted decoration-amber-400 underline-offset-2`}
                  title="This order shipped without production being recorded"
                  data-testid={`so-unrecorded-badge-${so.id}`}>No production recorded</button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-80 rounded-sm p-3" onClick={(e) => e.stopPropagation()} data-testid={`so-unrecorded-popover-${so.id}`}>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Shipped Without Recorded Production</p>
                {unrecorded.map((l) => {
                  const shippedCases = shippedFor(l);
                  return (
                    <div key={l.id} className="border-b border-slate-100 py-1.5 last:border-b-0">
                      <p className="truncate text-xs font-medium text-slate-700">{l.product_name}{l.design_name ? ` — ${l.design_name}` : ""}{l.case_pack ? ` — ${Math.round(l.case_pack)}-Pack` : ""}</p>
                      <div className="mt-0.5 flex items-end justify-between gap-2">
                        <p className="text-[11px] tabular-nums text-slate-500">{num(shippedCases)} cases shipped, no MO linked</p>
                        {onRecordProduction && (
                          <button type="button" onClick={() => onRecordProduction({ so, line: l, cases: shippedCases })}
                            className="whitespace-nowrap rounded-sm bg-[#002FA7] px-2 py-0.5 text-[10px] font-semibold text-white transition-colors hover:bg-[#00227a]"
                            data-testid={`so-record-production-${so.id}-${l.id}`}>Record production</button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </PopoverContent>
            </Popover>
          );
        })()}
        {mos.map((mo) => (
          <button key={`doc-${mo.id}`} type="button" onClick={(e) => { e.stopPropagation(); printProductionOrder(mo); }}
            title={`${moNum(mo.mwo_number)} — Production Order document`}
            className={`${badge} gap-1 border border-slate-300 bg-white text-slate-600 transition-colors hover:border-[#002FA7] hover:text-[#002FA7]`}
            data-testid={`so-mo-doc-chip-${so.id}-${mo.id}`}>
            <FileText className="h-3 w-3" /> {moNum(mo.mwo_number).replace(/^MO-\d{4}-/, "MO-")}
          </button>
        ))}
      </div>
      {so._dv && (so._dv.shipped > 0 || so._dv.planned > 0) && (
        <div className="mt-1" data-testid={`so-delivery-progress-${so.id}`}>
          {so._dv.shipped > 0 && (() => {
            const totalCases = so.lines.reduce((s, l) => s + (l.cases || 0), 0);
            const full = so._dv.shipped >= totalCases - 0.01;
            return (
              <>
                <div className={`flex items-center gap-1 whitespace-nowrap text-[11px] font-semibold ${full ? "text-emerald-600" : "text-sky-700"}`}
                  title="Cases already out on in-transit or delivered Delivery Orders">
                  <Truck className="h-3 w-3" /> {num(so._dv.shipped)} / {num(totalCases)} cases out
                </div>
                {!full && (
                  <div className="mt-0.5 h-1 w-24 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-sky-500" style={{ width: `${Math.min(100, (so._dv.shipped / totalCases) * 100)}%` }} />
                  </div>
                )}
              </>
            );
          })()}
          {so._dv.planned > 0 && (
            <div className="whitespace-nowrap text-[10px] text-slate-400" title="Cases on draft or scheduled Delivery Orders that haven't shipped yet">
              {num(so._dv.planned)} cases on pending DO
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LinePriceEditor({ so, line, products, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [price, setPrice] = useState("");
  const [unit, setUnit] = useState("case");
  const has = (line.price || 0) > 0;
  const total = linePriceTotal(line, products);
  const save = async () => {
    const p = parseFloat(price);
    if (isNaN(p) || p < 0) { toast.error("Enter a valid price"); return; }
    setBusy(true);
    try {
      await api.put(`/sales-orders/${so.id}/lines/${line.id}/price`, { price: p, price_unit: unit });
      setOpen(false);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to save price");
    } finally { setBusy(false); }
  };
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) { setPrice(has ? String(line.price) : ""); setUnit(line.price_unit || "case"); } }}>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} title="Set line price" className="group text-right" data-testid={`so-line-price-btn-${so.id}-${line.id}`}>
          {has ? (
            <>
              <p className="whitespace-nowrap font-semibold leading-tight tabular-nums text-slate-900 group-hover:text-[#002FA7]">{money(line.price)} <span className="text-[10px] font-normal text-slate-400">/ {line.price_unit || "case"}</span></p>
              {total != null && <p className="whitespace-nowrap text-[10px] leading-tight tabular-nums text-emerald-700">{money(total)} total</p>}
            </>
          ) : (
            <span className="inline-flex items-center gap-1 whitespace-nowrap text-[11px] font-semibold text-slate-400 transition-colors group-hover:text-[#002FA7]"><Plus className="h-3 w-3" /> Price</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 rounded-sm p-3" onClick={(e) => e.stopPropagation()}>
        <p className="mb-2 truncate text-xs font-semibold uppercase tracking-wide text-slate-500">Price — {line.product_name}</p>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
            <Input type="number" min="0" step="0.01" autoFocus value={price} onChange={(e) => setPrice(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") save(); }}
              className="h-8 rounded-sm pl-5 text-sm tabular-nums" placeholder="0.00" data-testid={`so-line-price-input-${so.id}-${line.id}`} />
          </div>
          <Select value={unit} onValueChange={setUnit}>
            <SelectTrigger className="h-8 w-[110px] rounded-sm text-xs" data-testid={`so-line-price-unit-${so.id}-${line.id}`}><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-sm">
              {PRICE_UNITS.map((u) => (<SelectItem key={u.value} value={u.value}>Per {u.label}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" onClick={save} disabled={busy} className="mt-2 w-full rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid={`so-line-price-save-${so.id}-${line.id}`}>
          {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null} Save Price
        </Button>
      </PopoverContent>
    </Popover>
  );
}

function SOLinesTable({ so, products, onWaive, compact = false, onChanged }) {
  return (
    <div className="overflow-x-auto border border-slate-200">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 text-left text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-600">
            <th className="px-3 py-2">Product</th>
            {compact ? (
              <>
                <th className="border-l border-slate-200 px-3 py-2 text-right">Truckloads</th>
                <th className="border-l border-slate-200 px-3 py-2 text-right">Pallets</th>
                <th className="border-l border-slate-200 px-3 py-2 text-right">Cases</th>
                <th className="border-l border-slate-200 px-3 py-2 text-right">Price</th>
              </>
            ) : (
              <>
                <th className="border-l border-slate-200 px-3 py-2 text-right">Cases</th>
                <th className="border-l border-slate-200 px-3 py-2 text-right">Bottles</th>
                <th className="border-l border-slate-200 px-3 py-2 text-right">Pallets</th>
                <th className="border-l border-slate-200 px-3 py-2 text-right">Truckloads</th>
                <th className="border-l border-slate-200 px-3 py-2 text-right">Out on DOs</th>
                <th className="border-l border-slate-200 px-3 py-2">Fulfillment</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {so.lines.map((l, i) => {
            const { pallets, trucks } = lineLogistics(l, products);
            return (
              <tr key={l.id || i} className="border-b border-slate-100" data-testid={`so-row-${so.id}-${i}`}>
                <td className="px-3 py-2">
                  <p className="font-medium leading-tight text-slate-800">{l.product_name}</p>
                  <p className="text-xs leading-tight text-slate-500">{l.design_name ? `${l.design_name} · ` : ""}{Math.round(l.case_pack)}pk</p>
                </td>
                {compact ? (
                  <>
                    <td className="border-l border-slate-100 px-3 py-2 text-right tabular-nums">
                      <p className="font-semibold leading-tight text-slate-900">{trucks != null ? round2(trucks) : "—"}</p>
                      <p className="text-[10px] leading-tight text-slate-400">{num(l.bottles)} bottles</p>
                    </td>
                    <td className="border-l border-slate-100 px-3 py-2 text-right tabular-nums text-slate-500">{pallets != null ? round2(pallets) : "—"}</td>
                    <td className="border-l border-slate-100 px-3 py-2 text-right tabular-nums text-slate-500">{num(l.cases)}</td>
                    <td className="border-l border-slate-100 px-3 py-2 text-right">
                      <LinePriceEditor so={so} line={l} products={products} onChanged={onChanged} />
                    </td>
                  </>
                ) : (
                  <>
                <td className="border-l border-slate-100 px-3 py-2 text-right font-semibold tabular-nums text-slate-900">{num(l.cases)}</td>
                <td className="border-l border-slate-100 px-3 py-2 text-right tabular-nums text-slate-500">{num(l.bottles)}</td>
                <td className="border-l border-slate-100 px-3 py-2 text-right tabular-nums text-slate-500">{pallets != null ? round2(pallets) : "—"}</td>
                <td className="border-l border-slate-100 px-3 py-2 text-right tabular-nums text-slate-500">{trucks != null ? round2(trucks) : "—"}</td>
                <td className="border-l border-slate-100 px-3 py-2 text-right tabular-nums" data-testid={`so-line-delivered-${so.id}-${i}`}>
                  {(() => {
                    const bl = so._dv?.byLine?.[l.id] || so._dv?.byLine?.[`${l.product_id}|${l.design_id || ""}|${l.config_id || ""}`];
                    return (
                      <>
                        {bl?.shipped ? <span className={`font-semibold ${bl.shipped >= (l.cases || 0) - 0.01 ? "text-emerald-600" : "text-sky-700"}`}>{num(bl.shipped)}</span> : <span className="text-slate-300">—</span>}
                        {bl?.planned ? <div className="text-[10px] text-slate-400">+{num(bl.planned)} pending</div> : null}
                      </>
                    );
                  })()}
                </td>
                <td className="border-l border-slate-100 px-3 py-2"><FulfillmentCell f={so._fs[i]} soId={so.id} idx={i} line={l} onWaive={onWaive} so={so} onChanged={onChanged} /></td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
        {compact && so.lines.some((l) => (l.price || 0) > 0) && (
          <tfoot>
            <tr className="border-t border-slate-200 bg-slate-50">
              <td colSpan={4} className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
                Order total{" "}
                <span className="ml-1 text-sm normal-case tabular-nums text-emerald-700" data-testid={`so-lines-total-${so.id}`}>
                  {money(so.lines.reduce((s, l) => s + (linePriceTotal(l, products) || 0), 0))}
                </span>
              </td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

function SODetailDialog({ so, products, onOpenChange, onEdit, onPrint, onWaive, onChanged }) {
  if (!so) return null;
  return (
    <Dialog open={!!so} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-sm sm:max-w-5xl" data-testid="so-view-dialog">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-3 font-heading tracking-tight">
            {so.so_number}
            <span className={`rounded-sm px-2 py-0.5 text-xs font-medium ${so._ops.cls}`}>{so._ops.label}</span>
            {so._ops.key === "ready" && so._ops.risk && (
              <span className="rounded-sm border border-red-300 bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700" data-testid="so-ready-past-due-badge">Past Due</span>
            )}
          </DialogTitle>
          <DialogDescription>
            {so.customer_name} · PO {so.customer_po || "—"} · RDD {fmtLong(so.ship_date)}
          </DialogDescription>
        </DialogHeader>
        <SOLinesTable so={so} products={products} onWaive={onWaive} onChanged={onChanged} />
        {so._dv?.dos?.length > 0 && (
          <div className="border border-slate-200" data-testid="so-do-list">
            <div className="border-b border-slate-200 bg-slate-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-600">Delivery Orders</div>
            {so._dv.dos.map((d) => (
              <div key={d.id} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 border-b border-slate-100 px-3 py-1.5 text-xs last:border-b-0" data-testid={`so-do-item-${d.id}`}>
                <Link to="/delivery-orders" className="font-semibold text-[#002FA7] hover:underline">{d.do_number}</Link>
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${doStatusMeta(d.status, (so.pickup_delivery || "") === "pickup").cls}`}>{doStatusMeta(d.status, (so.pickup_delivery || "") === "pickup").label}</span>
                <span className="tabular-nums text-slate-600">{num(d.cases)} cases</span>
                {d.pickup_date && <span className="text-slate-400">Pickup {fmtLong(d.pickup_date)}</span>}
              </div>
            ))}
          </div>
        )}
        <div className="border border-slate-200">
          <ExpandedDetail so={so} ops={so._ops} fs={so._fs} />
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" className="rounded-sm" onClick={onPrint} data-testid="so-view-print"><Printer className="mr-1.5 h-3.5 w-3.5" /> Print</Button>
          <Button size="sm" className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" onClick={onEdit} data-testid="so-view-edit"><Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DateCell({ so, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(so.ship_date || "");
  const save = async (v) => {
    setEditing(false);
    if (!v || v === (so.ship_date || "")) return;
    try {
      const { data } = await api.put(`/sales-orders/${so.id}/ship-date`, { ship_date: v });
      toast.success(`${so.so_number} delivery date changed to ${fmtLong(v)}`);
      if (data.mo_dates_synced?.length) toast.info(`Linked MO date${data.mo_dates_synced.length > 1 ? "s" : ""} moved to match: ${data.mo_dates_synced.join(", ")}`);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to update delivery date");
    }
  };
  if (editing) {
    return (
      <Input type="date" autoFocus value={val}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setVal(e.target.value)}
        onBlur={() => save(val)}
        onKeyDown={(e) => { if (e.key === "Enter") save(val); if (e.key === "Escape") setEditing(false); }}
        className="h-8 w-36 rounded-sm border-slate-300 text-sm"
        data-testid={`so-date-input-${so.id}`} />
    );
  }
  const t = today();
  const done = so.status !== "open";
  const diff = so.ship_date ? Math.round((new Date(`${so.ship_date}T00:00:00`) - new Date(`${t}T00:00:00`)) / 86400000) : null;
  let ctx = null, ctxCls = "text-slate-400";
  if (!done && diff != null) {
    if (diff < 0) { ctx = "Past Due"; ctxCls = "font-semibold text-red-600"; }
    else if (diff === 0) ctx = "Today";
    else if (diff === 1) ctx = "Tomorrow";
    else if (diff <= 14) ctx = `${diff} days`;
  }
  return (
    <button type="button" title="Change requested delivery date"
      onClick={(e) => { e.stopPropagation(); setVal(so.ship_date || ""); setEditing(true); }}
      className="group whitespace-nowrap text-left" data-testid={`so-date-btn-${so.id}`}>
      <span className="inline-flex items-center gap-1.5 text-slate-800 group-hover:text-[#002FA7]">
        {so.ship_date ? fmtLong(so.ship_date) : "—"}
        <Pencil className="h-3 w-3 text-slate-300 transition-colors group-hover:text-[#002FA7]" />
      </span>
      {ctx && <div className={`text-xs ${ctxCls}`}>{ctx}</div>}
    </button>
  );
}

function PoDateCell({ so, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(so.order_date || "");
  const save = async (v) => {
    setEditing(false);
    if (!v || v === (so.order_date || "")) return;
    try {
      await api.put(`/sales-orders/${so.id}/order-date`, { order_date: v });
      toast.success(`${so.so_number} PO date changed to ${fmtLong(v)}`);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to update PO date");
    }
  };
  if (editing) {
    return (
      <Input type="date" autoFocus value={val}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setVal(e.target.value)}
        onBlur={() => save(val)}
        onKeyDown={(e) => { if (e.key === "Enter") save(val); if (e.key === "Escape") setEditing(false); }}
        className="h-8 w-36 rounded-sm border-slate-300 text-sm"
        data-testid={`so-po-date-input-${so.id}`} />
    );
  }
  return (
    <button type="button" title="Change PO date"
      onClick={(e) => { e.stopPropagation(); setVal(so.order_date || ""); setEditing(true); }}
      className="group whitespace-nowrap text-left" data-testid={`so-po-date-btn-${so.id}`}>
      <span className="inline-flex items-center gap-1.5 text-slate-800 group-hover:text-[#002FA7]">
        {so.order_date ? fmtLong(so.order_date) : "—"}
        <Pencil className="h-3 w-3 text-slate-300 transition-colors group-hover:text-[#002FA7]" />
      </span>
    </button>
  );
}

const fmt12h = (t) => {
  const [h, m] = String(t || "").split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return "";
  const ap = h >= 12 ? "PM" : "AM";
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${ap}`;
};

function TimeCell({ so, field, endpoint, label, onSaved }) {
  const [open, setOpen] = useState(false);
  const cur = so[field] || "";
  const parse = (t) => {
    const match = /^(\d{1,2}):(\d{2})$/.exec(String(t || "").trim());
    if (!match) return { h: "", m: "00", ap: "AM" };
    const h24 = Number(match[1]);
    return { h: String(((h24 + 11) % 12) + 1), m: match[2], ap: h24 >= 12 ? "PM" : "AM" };
  };
  const [sel, setSel] = useState(parse(cur));
  const save = async (v) => {
    setOpen(false);
    if (v === cur) return;
    try {
      await api.put(`/sales-orders/${so.id}/${endpoint}`, { time: v });
      toast.success(v ? `${so.so_number} ${label} set to ${fmt12h(v)}` : `${so.so_number} ${label} cleared`);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || `Failed to update ${label}`);
    }
  };
  const apply = () => {
    if (!sel.h) { save(""); return; }
    let h24 = Number(sel.h) % 12;
    if (sel.ap === "PM") h24 += 12;
    save(`${String(h24).padStart(2, "0")}:${sel.m || "00"}`);
  };
  const selCls = "h-7 rounded-sm border border-slate-300 bg-white px-1 text-xs text-slate-800 focus:border-[#002FA7] focus:outline-none";
  return (
    <Popover open={open} onOpenChange={(o) => { if (o) setSel(parse(cur)); setOpen(o); }}>
      <PopoverTrigger asChild>
        <button type="button" title={`Set ${label}`} onClick={(e) => e.stopPropagation()}
          className="group whitespace-nowrap text-left" data-testid={`so-${endpoint}-btn-${so.id}`}>
          {cur
            ? <span className="inline-flex items-center gap-1 text-slate-600 group-hover:text-[#002FA7]"><Clock className="h-3 w-3 text-slate-300 group-hover:text-[#002FA7]" />{fmt12h(cur)}</span>
            : <Clock className="h-3.5 w-3.5 text-slate-300 transition-colors group-hover:text-[#002FA7]" />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto rounded-sm p-2" onClick={(e) => e.stopPropagation()}>
        <p className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-slate-400">{label}</p>
        <div className="flex items-center gap-1">
          <select value={sel.h} onChange={(e) => setSel((s) => ({ ...s, h: e.target.value }))} className={selCls} data-testid={`so-${endpoint}-hour-${so.id}`}>
            <option value="">--</option>
            {Array.from({ length: 12 }, (_, i) => String(i + 1)).map((h) => <option key={h} value={h}>{h}</option>)}
          </select>
          <span className="text-slate-400">:</span>
          <select value={sel.m} onChange={(e) => setSel((s) => ({ ...s, m: e.target.value }))} className={selCls} data-testid={`so-${endpoint}-min-${so.id}`}>
            {["00", "15", "30", "45"].map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <select value={sel.ap} onChange={(e) => setSel((s) => ({ ...s, ap: e.target.value }))} className={selCls} data-testid={`so-${endpoint}-ampm-${so.id}`}>
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
          <Button size="sm" className="h-7 rounded-sm bg-[#002FA7] px-2 text-xs text-white hover:bg-[#002080]" onClick={apply} data-testid={`so-${endpoint}-set-${so.id}`}>Set</Button>
          {cur && <Button size="sm" variant="ghost" className="h-7 rounded-sm px-2 text-xs text-slate-500 hover:text-red-600" onClick={() => save("")} data-testid={`so-${endpoint}-clear-${so.id}`}>Clear</Button>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function PickupDateCell({ so, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(so.pickup_date || "");
  const save = async (v) => {
    setEditing(false);
    if (v === (so.pickup_date || "")) return;
    try {
      await api.put(`/sales-orders/${so.id}/pickup-date`, { pickup_date: v });
      toast.success(v ? `${so.so_number} pickup date set to ${fmtLong(v)} — BOL Ship/Pickup Date updated` : `${so.so_number} pickup date cleared`);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to update pickup date");
    }
  };
  if (editing) {
    return (
      <Input type="date" autoFocus value={val}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setVal(e.target.value)}
        onBlur={() => save(val)}
        onKeyDown={(e) => { if (e.key === "Enter") save(val); if (e.key === "Escape") setEditing(false); }}
        className="h-8 w-36 rounded-sm border-slate-300 text-sm"
        data-testid={`so-pud-input-${so.id}`} />
    );
  }
  return (
    <button type="button" title="Set pickup date — drives the Ship/Pickup Date on linked BOLs"
      onClick={(e) => { e.stopPropagation(); setVal(so.pickup_date || ""); setEditing(true); }}
      className="group whitespace-nowrap text-left" data-testid={`so-pud-btn-${so.id}`}>
      <span className="inline-flex items-center gap-1.5 text-slate-800 group-hover:text-[#002FA7]">
        {so.pickup_date ? fmtLong(so.pickup_date) : "—"}
        <Pencil className="h-3 w-3 text-slate-300 transition-colors group-hover:text-[#002FA7]" />
      </span>
    </button>
  );
}

function DeliveryDateCell({ so, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(so.delivery_date || "");
  const save = async (v) => {
    setEditing(false);
    if (v === (so.delivery_date || "")) return;
    try {
      await api.put(`/sales-orders/${so.id}/delivery-date`, { delivery_date: v });
      toast.success(v ? `${so.so_number} delivery date set to ${fmtLong(v)}` : `${so.so_number} delivery date cleared`);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to update delivery date");
    }
  };
  if (editing) {
    return (
      <Input type="date" autoFocus value={val}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setVal(e.target.value)}
        onBlur={() => save(val)}
        onKeyDown={(e) => { if (e.key === "Enter") save(val); if (e.key === "Escape") setEditing(false); }}
        className="h-8 w-36 rounded-sm border-slate-300 text-sm"
        data-testid={`so-dd-input-${so.id}`} />
    );
  }
  const late = so.delivery_date && so.ship_date && so.delivery_date > so.ship_date;
  return (
    <button type="button" title="Set actual delivery date"
      onClick={(e) => { e.stopPropagation(); setVal(so.delivery_date || ""); setEditing(true); }}
      className="group whitespace-nowrap text-left" data-testid={`so-dd-btn-${so.id}`}>
      <span className={`inline-flex items-center gap-1.5 group-hover:text-[#002FA7] ${late ? "text-red-600" : "text-slate-800"}`}>
        {so.delivery_date ? fmtLong(so.delivery_date) : "—"}
        <Pencil className="h-3 w-3 text-slate-300 transition-colors group-hover:text-[#002FA7]" />
      </span>
    </button>
  );
}

function SoShipToCell({ so, onSaved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [val, setVal] = useState("");
  const has = !!(so.ship_to || "").trim();
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/sales-orders/${so.id}/ship-to`, { ship_to: val });
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to save ship-to");
    } finally { setBusy(false); }
  };
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setVal(so.ship_to || ""); }}>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} title="Edit ship-to" className="group max-w-[180px] text-left" data-testid={`so-ship-to-btn-${so.id}`}>
          {has ? (
            <span className="block whitespace-pre-line text-xs leading-snug text-slate-600 group-hover:text-[#002FA7]">{so.ship_to}</span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-400 transition-colors group-hover:text-[#002FA7]"><Plus className="h-3 w-3" /> Ship to</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 rounded-sm p-3" onClick={(e) => e.stopPropagation()}>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Ship To — {so.so_number}</p>
        <Textarea value={val} onChange={(e) => setVal(e.target.value)} rows={4} autoFocus
          placeholder={"Company / DC name\nStreet address\nCity, State ZIP"}
          className="rounded-sm text-sm" data-testid={`so-ship-to-input-${so.id}`} />
        <Button size="sm" onClick={save} disabled={busy} className="mt-2 w-full rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid={`so-ship-to-save-${so.id}`}>
          {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null} Save Ship To
        </Button>
      </PopoverContent>
    </Popover>
  );
}

function SoTermsEditor({ so, onSaved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [val, setVal] = useState("");
  const has = !!(so.terms || "").trim();
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/sales-orders/${so.id}/terms`, { terms: val });
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to save terms");
    } finally { setBusy(false); }
  };
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setVal(so.terms || ""); }}>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} title="Payment terms" className="group block text-left" data-testid={`so-terms-btn-${so.id}`}>
          {has ? (
            <span className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-wide text-slate-500 group-hover:text-[#002FA7]">{so.terms}</span>
          ) : (
            <span className="inline-flex items-center gap-1 whitespace-nowrap text-[10px] font-semibold text-slate-400 transition-colors group-hover:text-[#002FA7]"><Plus className="h-2.5 w-2.5" /> Terms</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 rounded-sm p-3" onClick={(e) => e.stopPropagation()}>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Payment Terms</p>
        <Input value={val} onChange={(e) => setVal(e.target.value)} autoFocus placeholder="e.g. NET 30"
          onKeyDown={(e) => { if (e.key === "Enter") save(); }}
          className="h-8 rounded-sm text-sm" data-testid={`so-terms-input-${so.id}`} />
        <Button size="sm" onClick={save} disabled={busy} className="mt-2 w-full rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid={`so-terms-save-${so.id}`}>
          {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null} Save Terms
        </Button>
      </PopoverContent>
    </Popover>
  );
}

const PRICE_UNITS = [
  { value: "unit", label: "Unit" },
  { value: "case", label: "Case" },
  { value: "pallet", label: "Pallet" },
  { value: "truckload", label: "Truckload" },
];

const money = (v) => "$" + v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function lineQty(l, products, unit) {
  if (unit === "unit") return l.bottles || 0;
  if (unit === "case") return l.cases || 0;
  const { pallets, trucks } = lineLogistics(l, products);
  return unit === "pallet" ? pallets : trucks;
}

function linePriceTotal(l, products) {
  if (!(l.price > 0)) return null;
  const qty = lineQty(l, products, l.price_unit || "case");
  return qty ? l.price * qty : null;
}

function soPriceTotal(so, products) {
  const lineTotals = so.lines.map((l) => linePriceTotal(l, products)).filter((v) => v != null);
  if (lineTotals.length) return lineTotals.reduce((s, v) => s + v, 0);
  if (!(so.price > 0)) return null;
  const unit = so.price_unit || "case";
  let qty = null;
  if (unit === "unit") qty = so.lines.reduce((s, l) => s + (l.bottles || 0), 0);
  else if (unit === "case") qty = so.lines.reduce((s, l) => s + (l.cases || 0), 0);
  else {
    const calcs = so.lines.map((l) => lineLogistics(l, products));
    if (unit === "pallet") qty = calcs.some((c) => c.pallets != null) ? calcs.reduce((s, c) => s + (c.pallets || 0), 0) : null;
    else qty = calcs.some((c) => c.trucks != null) ? calcs.reduce((s, c) => s + (c.trucks || 0), 0) : null;
  }
  if (qty == null || qty <= 0) return null;
  return so.price * qty;
}

function SoPriceCell({ so, products, onSaved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [price, setPrice] = useState("");
  const [unit, setUnit] = useState("case");
  const save = async () => {
    const p = parseFloat(price);
    if (isNaN(p) || p < 0) { toast.error("Enter a valid price"); return; }
    setBusy(true);
    try {
      await api.put(`/sales-orders/${so.id}/price`, { price: p, price_unit: unit });
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to save price");
    } finally { setBusy(false); }
  };
  const has = (so.price || 0) > 0;
  const total = soPriceTotal(so, products);
  const hasLinePricing = so.lines.some((l) => (l.price || 0) > 0);
  const totalCases = so.lines.reduce((s, l) => s + (l.cases || 0), 0);
  const perCase = total != null && totalCases > 0 ? total / totalCases : null;
  if (hasLinePricing) {
    const pricedUnits = [...new Set(so.lines.filter((l) => (l.price || 0) > 0).map((l) => `${l.price}|${l.price_unit || "case"}`))];
    const uniformCase = pricedUnits.length === 1 && pricedUnits[0].endsWith("|case");
    return (
      <div className="whitespace-nowrap" data-testid={`so-price-btn-${so.id}`}>
        <p className="font-semibold tabular-nums text-emerald-700" data-testid={`so-price-total-${so.id}`}>{total != null ? `${money(total)} total` : "—"}</p>
        {perCase != null && (
          <p className="text-xs font-medium tabular-nums text-slate-600" data-testid={`so-price-percase-${so.id}`}>
            {uniformCase ? "" : "≈ "}{money(perCase)} <span className="font-normal text-slate-400">/ case</span>
          </p>
        )}
        <p className="text-[10px] text-slate-400">from line prices</p>
        <SoTermsEditor so={so} onSaved={onSaved} />
      </div>
    );
  }
  return (
    <div>
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) { setPrice(has ? String(so.price) : ""); setUnit(so.price_unit || "case"); } }}>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} title="Set price"
          className="group whitespace-nowrap text-left" data-testid={`so-price-btn-${so.id}`}>
          {has ? (
            <span className="inline-flex flex-col items-start">
              <span className="inline-flex items-center gap-1.5 font-medium tabular-nums text-slate-800 group-hover:text-[#002FA7]">
                ${so.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                <span className="text-xs font-normal text-slate-400">/ {so.price_unit || "case"}</span>
                <Pencil className="h-3 w-3 text-slate-300 transition-colors group-hover:text-[#002FA7]" />
              </span>
              {total != null && (
                <span className="text-xs font-semibold tabular-nums text-emerald-700" data-testid={`so-price-total-${so.id}`}>
                  ${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} total
                </span>
              )}
              {(so.price_unit || "case") !== "case" && perCase != null && (
                <span className="text-xs font-medium tabular-nums text-slate-600" data-testid={`so-price-percase-${so.id}`}>
                  ≈ {money(perCase)} <span className="font-normal text-slate-400">/ case</span>
                </span>
              )}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-400 transition-colors group-hover:text-[#002FA7]">
              <Plus className="h-3 w-3" /> Set price
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 rounded-sm p-3" onClick={(e) => e.stopPropagation()}>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Price</p>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
            <Input type="number" min="0" step="0.01" autoFocus value={price} onChange={(e) => setPrice(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") save(); }}
              className="h-8 rounded-sm pl-5 text-sm tabular-nums" placeholder="0.00" data-testid={`so-price-input-${so.id}`} />
          </div>
          <Select value={unit} onValueChange={setUnit}>
            <SelectTrigger className="h-8 w-[110px] rounded-sm text-xs" data-testid={`so-price-unit-${so.id}`}><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-sm">
              {PRICE_UNITS.map((u) => (<SelectItem key={u.value} value={u.value} data-testid={`so-price-unit-${u.value}-${so.id}`}>Per {u.label}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" onClick={save} disabled={busy} className="mt-2 w-full rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid={`so-price-save-${so.id}`}>
          {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null} Save Price
        </Button>
      </PopoverContent>
    </Popover>
    <SoTermsEditor so={so} onSaved={onSaved} />
    </div>
  );
}

function FulfillmentCell({ f, soId, idx, line, onWaive, so, onChanged }) {
  const badge = "inline-flex items-center gap-1 whitespace-nowrap rounded-full px-1.5 py-0.5 text-[10px] font-semibold";
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={`so-fulfillment-${soId}-${idx}`}>
      <span className={`${badge} ${f.allocated > 0 ? "bg-[#002FA7]/10 text-[#002FA7]" : "bg-slate-100 text-slate-400"}`} title="Stock allocated to this line">{num(f.allocated)} Allocated</span>
      {f.remaining > 0.01 && (
        <span className={`${badge} bg-slate-100 text-slate-600`} title="Still needed on this line" data-testid={`so-remaining-${soId}-${idx}`}>{num(f.remaining)} Remaining</span>
      )}
      {f.remaining > 0.01 && f.available > 0.01 && (
        <span className={`${badge} bg-emerald-50 text-emerald-700`} title="Free stock of this exact product/design/pack" data-testid={`so-available-${soId}-${idx}`}>{num(f.available)} Available</span>
      )}
      {f.remaining <= 0.01 && (line?.cases || 0) > 0 && (
        <span className={`${badge} bg-emerald-100 text-emerald-800`} title="Line fully allocated" data-testid={`so-line-ready-${soId}-${idx}`}>Ready</span>
      )}
      {so && so.status === "open" && onChanged && <AllocateControl so={so} line={line} f={f} onChanged={onChanged} testId={`so-line-allocate-${soId}-${idx}`} />}
      {(line?.copacker_cases || 0) > 0.01 && (
        <span className={`${badge} bg-violet-50 text-violet-700`} title="Finished goods held at a co-packer — counted in availability" data-testid={`so-copacker-${soId}-${idx}`}>
          {num(line.copacker_cases)} At Co-Packer
        </span>
      )}
      {(f.inProd > 0 || f.shortage > 0) && (
        <span className={`${badge} ${f.inProd > 0 ? "bg-orange-50 text-orange-700" : "bg-slate-100 text-slate-400"}`}>{num(f.inProd)} Production</span>
      )}
      {f.mo && (
        <Link to="/production-planning" onClick={(e) => e.stopPropagation()} className="whitespace-nowrap text-[11px] font-semibold text-[#002FA7] hover:underline" data-testid={`so-mo-link-${soId}-${idx}`}>
          {moNum(f.mo.mwo_number)}
        </Link>
      )}
      {f.uncovered > 0.01 && <span className={`${badge} bg-red-100 text-red-700`}>{num(f.uncovered)} Short</span>}
      {f.waived > 0.01 && (
        <span className={`${badge} bg-slate-100 text-slate-500`} title="Shortage accepted — no production will be scheduled" data-testid={`so-waived-${soId}-${idx}`}>
          {num(f.waived)} Waived
        </span>
      )}
      {line && onWaive && !line.no_auto_mo && f.shortage > 0.01 && (
        <button type="button" title="Waive production — accept this shortage and remove the MO from the queue"
          onClick={(e) => { e.stopPropagation(); onWaive(line, true); }}
          className="text-slate-300 transition-colors hover:text-slate-600" data-testid={`so-waive-${soId}-${idx}`}>
          <Ban className="h-3.5 w-3.5" />
        </button>
      )}
      {line && onWaive && line.no_auto_mo && (
        <button type="button" title="Resume production — recreate an MO for this shortage"
          onClick={(e) => { e.stopPropagation(); onWaive(line, false); }}
          className="text-slate-300 transition-colors hover:text-[#002FA7]" data-testid={`so-unwaive-${soId}-${idx}`}>
          <RotateCcw className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function PriorityCell({ so, onChanged }) {
  const p = so.priority || 3;
  return (
    <Select value={String(p)} onValueChange={async (v) => {
      try {
        await api.put(`/sales-orders/${so.id}/priority`, { priority: Number(v) });
        onChanged(so.id, Number(v));
      } catch { toast.error("Failed to update priority"); }
    }}>
      <SelectTrigger onClick={(e) => e.stopPropagation()} data-testid={`so-priority-${so.id}`}
        className={`h-6 w-auto gap-1 rounded-full border px-2 py-0 text-[10px] font-semibold ${PRIORITY_META[p].cls}`}>
        <span>{PRIORITY_META[p].short}</span>
      </SelectTrigger>
      <SelectContent>
        {[1, 2, 3, 4].map((k) => (<SelectItem key={k} value={String(k)}>{PRIORITY_META[k].label}</SelectItem>))}
      </SelectContent>
    </Select>
  );
}

function ExpandedDetail({ so, ops, fs }) {
  const stock = fs.reduce((s, f) => s + f.stock, 0);
  const shortage = fs.reduce((s, f) => s + f.shortage, 0);
  const inProd = fs.reduce((s, f) => s + f.inProd, 0);
  const uncovered = fs.reduce((s, f) => s + f.uncovered, 0);
  const waived = fs.reduce((s, f) => s + (f.waived || 0), 0);
  const mos = fs.filter((f) => f.mo).map((f) => f.mo);
  const row = (k, v) => (
    <div className="flex justify-between gap-3 text-xs"><span className="text-slate-500">{k}</span><span className="font-medium text-slate-800">{v}</span></div>
  );
  return (
    <div className="grid grid-cols-1 gap-4 bg-slate-50/70 px-6 py-4 sm:grid-cols-3" data-testid={`so-detail-${so.id}`}>
      <div className="space-y-1.5">
        <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">Order Information</p>
        {row("Sales Order #", so.so_number)}
        {row("Customer", so.customer_name || "—")}
        {row("Customer PO #", so.customer_po || "—")}
        {row("PO Status", PO_STATUS_META[so.po_status || "pending_review"]?.label || "Pending Review")}
        {row("Order Date", so.order_date || "—")}
        {row("Deliver / Pickup", so.ship_date || "—")}
        {row("Admin Status", SO_STATUS_META[so.status]?.label || so.status)}
        {(so.attachments || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {so.attachments.map((a, i) => (
              <a key={`${a.path}-${i}`} href={`${process.env.REACT_APP_BACKEND_URL}${a.url}`} target="_blank" rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center gap-1 rounded-sm border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-[#002FA7] hover:bg-slate-100"
                data-testid={`so-card-attachment-${so.id}-${i}`}>
                <Paperclip className="h-2.5 w-2.5" /> {a.name}
              </a>
            ))}
          </div>
        )}
        {so.notes && <p className="pt-1 text-[11px] italic text-slate-500">{so.notes}</p>}
      </div>
      <div className="space-y-1.5">
        <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">Fulfillment</p>
        {row("Ordered Cases", num(so.total_cases || 0))}
        {row("Reserved Finished Goods", num(stock))}
        {row("Production Required", num(shortage))}
        {row("Covered by MOs", num(inProd))}
        {waived > 0.01 && row("Waived (no production)", num(waived))}
        <div className="flex justify-between gap-3 text-xs">
          <span className="text-slate-500">Uncovered</span>
          <span className={`font-bold ${uncovered > 0.01 ? "text-red-600" : "text-emerald-600"}`}>{num(uncovered)}</span>
        </div>
        {ops.reasons.filter((r) => !/ short$/i.test(r)).length > 0 && (
          <div className="pt-1.5" data-testid={`so-risk-reasons-${so.id}`}>
            {ops.reasons.filter((r) => !/ short$/i.test(r)).map((r) => (
              <p key={r} className="flex items-center gap-1 text-[11px] font-semibold text-red-600"><AlertTriangle className="h-3 w-3" /> {r}</p>
            ))}
          </div>
        )}
      </div>
      <div className="space-y-2">
        <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">Manufacturing</p>
        {mos.length === 0 ? (
          <p className="text-xs text-slate-400">No linked manufacturing orders.</p>
        ) : mos.map((mo) => {
          const shorts = (mo.availability || []).filter((a) => (a.shortage || 0) > 0);
          return (
            <div key={mo.id} className="border border-slate-200 bg-white p-2.5 text-xs" data-testid={`so-detail-mo-${mo.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <Link to="/production-planning" onClick={(e) => e.stopPropagation()} className="font-semibold text-[#002FA7] hover:underline">{moNum(mo.mwo_number)}</Link>
                <span className="text-slate-600">{num(mo.case_pack ? (mo.bottle_quantity || 0) / mo.case_pack : 0)} cases</span>
                <span className="text-slate-400">{fmtShort(mo.production_date)}</span>
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${mo.status === "Completed" ? "bg-emerald-50 text-emerald-700" : mo.status === "In Production" || mo.status === "Partially Completed" ? "bg-orange-50 text-orange-700" : "bg-slate-100 text-slate-600"}`}>{mo.status}</span>
              </div>
              <div className="mt-1">
                {shorts.length === 0 ? (
                  <p className="flex items-center gap-1 text-[11px] text-emerald-600"><CheckCircle2 className="h-3 w-3" /> Components available</p>
                ) : shorts.map((a, i) => (
                  <p key={`${a.type || ""}-${a.name || i}`} className="flex items-center gap-1 text-[11px] font-semibold text-red-600"><AlertTriangle className="h-3 w-3" /> {num(a.shortage)} {a.type ? `${a.type} ` : ""}{a.name} short</p>
                ))}
                {mo.availability_live && <p className="mt-0.5 text-[10px] text-slate-400" data-testid={`so-readiness-live-${mo.id}`}>Checked against current inventory</p>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


export default function SalesOrders() {
  const [sos, setSos] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [moMap, setMoMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("all");
  const [fRisk, setFRisk] = useState("all");
  const [fCustomer, setFCustomer] = useState("all");
  const [fProduct, setFProduct] = useState("all");
  const [fOps, setFOps] = useState("all");
  const [fPriority, setFPriority] = useState("all");
  const [fPickup, setFPickup] = useState("all");
  const [poCreating, setPoCreating] = useState(false);
  const [poDragOver, setPoDragOver] = useState(false);
  const createFromPo = async (f) => {
    setPoCreating(true);
    toast.info(`Reading ${f.name} and building the order…`);
    try {
      const d = await uploadDocument(f);
      await api.post("/sales-orders/from-po", { path: d.path, name: d.name || f.name, url: d.url }).then(({ data }) => {
        toast.success(`${data.so_number} created from PO${data.customer_po ? ` ${data.customer_po}` : ""} — ${data.customer_name}, ${data.lines.length} line${data.lines.length !== 1 ? "s" : ""}`, { duration: 10000 });
      });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Couldn't create an order from that PO");
    } finally { setPoCreating(false); }
  };
  const [fDueBy, setFDueBy] = useState("");
  const [fRange, setFRange] = useState("all");
  const [fPaperwork, setFPaperwork] = useState("all");
  const [fShipment, setFShipment] = useState("all");
  const [sort, setSort] = useState({ key: "po_date", dir: "desc" });
  const [viewSoId, setViewSoId] = useState(null);
  const [doFromSo, setDoFromSo] = useState(null);
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [delSo, setDelSo] = useState(null);
  const [doMap, setDoMap] = useState({});
  const companyRef = useRef(null);

  const getCompany = async () => {
    if (!companyRef.current) {
      try { companyRef.current = (await api.get("/company-settings")).data; } catch { companyRef.current = {}; }
    }
    return companyRef.current;
  };

  const printSO = async (so) => {
    printSalesOrder(so, await getCompany(), customers.find((c) => c.id === so.customer_id) || null);
  };

  const [bolSo, setBolSo] = useState(null);
  const [invSo, setInvSo] = useState(null);
  const [editBol, setEditBol] = useState(null);
  const [carriers, setCarriers] = useState([]);
  const [landFreight, setLandFreight] = useState([]);

  const load = async () => {
    try {
      const [s, c, p, m, comps, resv, dv, lf] = await Promise.all([
        api.get("/sales-orders"), api.get("/customers"), api.get("/products"), api.get("/manufacturing-orders"),
        api.get("/components").catch(() => ({ data: [] })),
        api.get("/components/reservations").catch(() => ({ data: { by_key: {} } })),
        api.get("/delivery-orders").catch(() => ({ data: [] })),
        api.get("/land-freight").catch(() => ({ data: [] })),
      ]);
      setCarriers((lf.data || []).map((x) => x.company_name).filter(Boolean));
      setLandFreight(lf.data || []);
      setSos(s.data);
      s.data.forEach((so) => (so.mo_actions || []).forEach((a) => toast.info(a.message)));
      setCustomers(c.data);
      setProducts(p.data.filter((x) => (x.pack_configs || []).length));
      const compIndex = Object.fromEntries(comps.data.map((x) => [x.id, x]));
      const byKey = resv.data.by_key || {};
      setMoMap(Object.fromEntries(m.data.map((mo) => [mo.id, withLiveAvailability(mo, compIndex, byKey)])));
      const dvm = {};
      dv.data.forEach((d) => {
        if (d.status === "cancelled" || !d.so_id) return;
        const rec = dvm[d.so_id] || (dvm[d.so_id] = { shipped: 0, planned: 0, byLine: {}, dos: [] });
        const out = d.status === "dispatched" || d.status === "completed";
        (d.lines || []).forEach((l) => {
          const key = l.so_line_id || `${l.product_id}|${l.design_id || ""}|${l.config_id || ""}`;
          const bl = rec.byLine[key] || (rec.byLine[key] = { shipped: 0, planned: 0 });
          if (out) { rec.shipped += l.cases || 0; bl.shipped += l.cases || 0; }
          else { rec.planned += l.cases || 0; bl.planned += l.cases || 0; }
        });
        rec.dos.push({ id: d.id, do_number: d.do_number, status: d.status, cases: d.total_cases, pickup_date: d.pickup_date, full: d });
      });
      setDoMap(dvm);
    } catch {
      toast.error("Failed to load sales orders");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const [shipFromOpts, setShipFromOpts] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const [cs, cps, dists] = await Promise.all([
          api.get("/company-settings"), api.get("/co-packers"), api.get("/distributors"),
        ]);
        const opts = [];
        const plant = cs.data?.company_name || cs.data?.bottler_name;
        if (plant) opts.push({ group: "Plant", name: plant, address: [cs.data?.address_line1, cs.data?.address_line2].filter(Boolean).join(", ") });
        (cps.data || []).forEach((c) => c.company_name && opts.push({ group: "Co-Packers", name: c.company_name, address: c.address || "" }));
        (dists.data || []).forEach((d) => d.company_name && opts.push({ group: "Distributors", name: d.company_name, address: d.address || "" }));
        setShipFromOpts(opts);
      } catch { /* dropdown falls back to current value only */ }
    })();
  }, []);

  // Enrich with derived operational data
  const enriched = useMemo(() => sos.map((so) => {
    const fs = (so.lines || []).map((l) => lineFul(l, moMap));
    return { ...so, _ops: deriveOps(so, moMap), _fs: fs, _dv: doMap[so.id] || null };
  }), [sos, moMap, doMap]);

  const t = today();
  const counts = useMemo(() => ({
    all: enriched.length,
    open: enriched.filter((s) => s.status === "open").length,
    ready: enriched.filter((s) => s.status === "open" && s._ops.key === "ready").length,
    shipped: enriched.filter((s) => s.status === "shipped").length,
    cancelled: enriched.filter((s) => s.status === "cancelled").length,
    atRisk: enriched.filter((s) => s.status === "open" && s._ops.risk).length,
    pastDue: enriched.filter((s) => s.status === "open" && s.ship_date && s.ship_date < t).length,
    onTime: enriched.filter((s) => s.status === "open" && !s._ops.risk).length,
  }), [enriched, t]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return enriched.filter((so) => {
      if (tab === "open" && so.status !== "open") return false;
      if (tab === "ready" && !(so.status === "open" && so._ops.key === "ready")) return false;
      if (tab === "shipped" && so.status !== "shipped") return false;
      if (tab === "cancelled" && so.status !== "cancelled") return false;
      if (fRisk === "at_risk" && !(so.status === "open" && so._ops.risk)) return false;
      if (fRisk === "past_due" && !(so.status === "open" && so.ship_date && so.ship_date < t)) return false;
      if (fRisk === "on_time" && !(so.status === "open" && !so._ops.risk)) return false;
      if (fCustomer !== "all" && so.customer_id !== fCustomer) return false;
      if (fProduct !== "all" && !so.lines.some((l) => l.product_id === fProduct)) return false;
      if (fOps !== "all" && so._ops.key !== fOps) return false;
      if (fPriority !== "all" && String(so.priority || 3) !== fPriority) return false;
      if (fPickup !== "all" && (so.pickup_delivery || "unset") !== fPickup) return false;
      if (fDueBy && (!so.ship_date || so.ship_date > fDueBy)) return false;
      if (fRange !== "all") {
        const d = poDateOf(so);
        if (!d || d < rangeStart(fRange)) return false;
      }
      if (fPaperwork !== "all" && !missingPaperwork(so, fPaperwork)) return false;
      if (fShipment !== "all") {
        const stats = (so._dv?.dos || []).map((d) => d.status);
        if (!stats.length) {
          if (fShipment !== "open" || so.status === "cancelled") return false;
        } else if (!stats.includes(fShipment)) return false;
      }
      if (term && ![so.so_number, so.customer_name, so.customer_po, ...so.lines.map((l) => `${l.product_name} ${l.design_name}`)].join(" ").toLowerCase().includes(term)) return false;
      return true;
    });
  }, [enriched, tab, fRisk, t, q, fCustomer, fProduct, fOps, fPriority, fPickup, fDueBy, fRange, fPaperwork, fShipment]);

  const dupPoSet = useMemo(() => {
    const counts = {};
    for (const so of enriched) {
      const po = (so.customer_po || "").trim();
      if (!po || so.status === "cancelled") continue;
      counts[po] = (counts[po] || 0) + 1;
    }
    return new Set(Object.keys(counts).filter((po) => counts[po] > 1));
  }, [enriched]);

  const revenue = useMemo(() => {
    let total = 0, priced = 0, count = 0, cases = 0;
    for (const so of filtered) {
      if (so.status === "cancelled") continue;
      count += 1;
      cases += so.lines.reduce((s, l) => s + (l.cases || 0), 0);
      const t = soPriceTotal(so, products);
      if (t != null) { total += t; priced += 1; }
    }
    return { total, priced, count, cases };
  }, [filtered, products]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sort.dir === "asc" ? 1 : -1;
    const byDate = (a, b) => (a.ship_date || "9999").localeCompare(b.ship_date || "9999");
    const byPri = (a, b) => (a.priority || 3) - (b.priority || 3);
    const byPoDate = (a, b) => poDateOf(a).localeCompare(poDateOf(b));
    if (sort.key === "date") arr.sort((a, b) => dir * byDate(a, b));
    else if (sort.key === "priority") arr.sort((a, b) => dir * byPri(a, b) || byDate(a, b));
    else if (sort.key === "po_date") arr.sort((a, b) => dir * byPoDate(a, b) || byDate(a, b));
    else if (sort.key === "dd") arr.sort((a, b) => {
      const av = a.delivery_date || "", bv = b.delivery_date || "";
      if (!av && !bv) return byDate(a, b);
      if (!av) return 1;
      if (!bv) return -1;
      return dir * av.localeCompare(bv) || byDate(a, b);
    });
    else arr.sort((a, b) => byPri(a, b) || byDate(a, b));
    const out = [];
    const used = new Set();
    for (const so of arr) {
      if (used.has(so.id)) continue;
      out.push(so); used.add(so.id);
      const po = (so.customer_po || "").trim();
      if (po && so.status !== "cancelled" && dupPoSet.has(po)) {
        for (const other of arr) {
          if (!used.has(other.id) && other.status !== "cancelled" && (other.customer_po || "").trim() === po) {
            out.push(other); used.add(other.id);
          }
        }
      }
    }
    return out;
  }, [filtered, sort, dupPoSet]);

  const PAGE_SIZE = 20;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [tab, fRisk, q, fCustomer, fProduct, fOps, fPriority, fPickup, fDueBy, fRange, fPaperwork, fShipment, sort]);
  const visibleRows = useMemo(() => (sorted.length > visibleCount ? sorted.slice(0, visibleCount) : sorted), [sorted, visibleCount]);
  const showMoreBar = sorted.length > visibleCount ? (
    <div className="flex items-center justify-center gap-3 border border-t-0 border-slate-200 bg-slate-50 py-2.5" data-testid="so-show-more-bar">
      <span className="text-xs tabular-nums text-slate-500">Showing {visibleRows.length} of {sorted.length} orders</span>
      <button type="button" onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
        className="rounded-sm border border-[#002FA7] px-3 py-1 text-xs font-semibold text-[#002FA7] hover:bg-[#002FA7]/5"
        data-testid="so-show-more">Show {Math.min(PAGE_SIZE, sorted.length - visibleCount)} more</button>
      <button type="button" onClick={() => setVisibleCount(sorted.length)}
        className="text-xs font-semibold text-slate-500 underline-offset-2 hover:text-[#002FA7] hover:underline"
        data-testid="so-show-all">Show all</button>
    </div>
  ) : null;

  const createMoForLine = async (so, l, cases, ctx) => {
    const { comps, compIndex, reservations } = ctx;
    const product = products.find((p) => p.id === l.product_id);
    if (!product) return null;
    const cfg = (product.pack_configs || []).find((c) => c.id === l.config_id) || product.pack_configs[0];
    const bottles = Math.round(cases * l.case_pack);
    const { data: mo } = await api.post("/manufacturing-orders", {
      product_id: l.product_id, config_id: cfg?.id || "", case_pack: l.case_pack,
      quantity: bottles, unit: "bottles",
      production_date: so.ship_date || new Date().toISOString().slice(0, 10),
      design_id: l.design_id || null, customer_id: so.customer_id, customer_name: so.customer_name,
      source_so_id: so.id, source_so_number: so.so_number,
    });
    const cpp = Number(cfg?.cases_per_pallet) || 0;
    const bpp = cpp * l.case_pack;
    const ppt = Number(cfg?.pallets_per_truckload) || 0;
    const totalPallets = bpp ? bottles / bpp : 0;
    const bom = buildOrderBOM(product, comps, { cfg, casePackSize: l.case_pack, totalCases: cases, totalPallets, totalTrucks: ppt && totalPallets ? totalPallets / ppt : 0, designId: l.design_id || "" });
    const { data: placed } = await api.post(`/manufacturing-orders/${mo.id}/place`, {
      bottle_size: product.name || "", ordered_bottles: bottles,
      bom_snapshot: bom, availability: buildAvailability(bom, compIndex, reservations),
    });
    await api.put(`/sales-orders/${so.id}/line-mo`, { line_id: l.id, mo_id: mo.id, mo_number: placed?.mwo_number || "" });
    return placed;
  };

  const buildMoCtx = async () => {
    const { data: comps } = await api.get("/components");
    const compIndex = Object.fromEntries(comps.map((c) => [c.id, c]));
    let reservations = {};
    try { reservations = (await api.get("/components/reservations")).data.by_key || {}; } catch { /* non-blocking */ }
    return { comps, compIndex, reservations };
  };

  // Auto-MO creation removed: shortages stay visible as "Short"; MOs are created
  // deliberately via the Create MO dialog (Short badge) or on the MO page.
  const [createMoFor, setCreateMoFor] = useState(null);
  const [creatingMo, setCreatingMo] = useState(false);
  const confirmCreateMos = async (rows) => {
    const so = createMoFor;
    if (!so || !rows.length) return;
    setCreatingMo(true);
    try {
      const ctx = await buildMoCtx();
      for (const { l, cases } of rows) {
        const placed = await createMoForLine(so, l, cases, ctx);
        if (placed) toast.success(`${moNum(placed.mwo_number)} created for ${num(cases)} cases (${so.so_number}) — components reserved`);
      }
      setCreateMoFor(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to create the manufacturing order");
    } finally { setCreatingMo(false); }
  };

  const recordProduction = async ({ so, line, cases }) => {
    try {
      const ctx = await buildMoCtx();
      const placed = await createMoForLine(so, line, cases, ctx);
      if (!placed) { toast.error("Product not found for this line"); return; }
      toast.success(`${moNum(placed.mwo_number)} created for ${num(cases)} shipped cases — confirm to enter production`);
      setProduceMo(placed);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to create the manufacturing order");
    }
  };

  const unproduceMo = async (mo) => {
    if (!window.confirm(`Reverse production for ${moNum(mo.mwo_number)}? Components go back into stock, the finished goods are removed, and the MO returns to Awaiting Production.`)) return;
    try {
      const { data } = await api.post(`/manufacturing-orders/${mo.id}/unproduce`);
      toast.success(data.message || "Production reversed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to reverse production");
    }
  };

  const [waiveTarget, setWaiveTarget] = useState(null);
  const [schedulingSo, setSchedulingSo] = useState(null);
  const [produceMo, setProduceMo] = useState(null);
  const [addStockTarget, setAddStockTarget] = useState(null);

  const scheduleShort = (so) => setCreateMoFor(so);

  const doWaive = async (so, line, waived) => {
    try {
      const { data } = await api.put(`/sales-orders/${so.id}/line-waive`, { line_id: line.id, waived });
      (data.mo_actions || []).forEach((a) => toast.success(a.message));
      if (waived) {
        toast.success(`${line.product_name} shortage waived — no production will be scheduled`);
        load();
      } else {
        toast.success("Production re-enabled for this line");
        load();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to update line");
    }
  };

  const changeStatus = async (so, status) => {
    try {
      const { data } = await api.put(`/sales-orders/${so.id}/status`, { status });
      if (status === "shipped") toast.success(`${so.so_number} marked shipped — reservation released. Adjust Units in Stock when the goods leave (shipments arrive with Delivery Orders).`);
      (data?.mo_actions || []).forEach((a) => (a.deleted ? toast.success(a.message) : toast.warning(a.message)));
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to update status");
    }
  };

  const confirmDelete = async () => {
    try {
      const { data } = await api.delete(`/sales-orders/${delSo.id}`);
      toast.success(`${delSo.so_number} deleted`);
      (data?.mo_actions || []).forEach((a) => (a.deleted ? toast.success(a.message) : toast.warning(a.message)));
      setDelSo(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to delete");
    }
  };

  const setPriorityLocal = (id, p) => setSos((prev) => prev.map((s) => (s.id === id ? { ...s, priority: p } : s)));

  const tableWrapRef = useRef(null);
  const tableRef = useRef(null);
  const [fitZoom, setFitZoom] = useState(1);
  const [zoomMode, setZoomMode] = useState("fit");
  const [manualZoom, setManualZoom] = useState(1);
  const tableZoom = zoomMode === "fit" ? fitZoom : manualZoom;
  useLayoutEffect(() => {
    const calc = () => {
      const wrap = tableWrapRef.current;
      const tbl = tableRef.current;
      if (!wrap || !tbl) return;
      tbl.style.zoom = 1;
      const natural = tbl.offsetWidth;
      if (!natural) return;
      let z = Math.min(1, Math.max(0.55, Math.floor((wrap.clientWidth / natural) * 100) / 100));
      tbl.style.zoom = z;
      let guard = 20;
      while (guard-- > 0 && z > 0.55 && wrap.scrollWidth > wrap.clientWidth + 1) {
        z = Math.round((z - 0.01) * 100) / 100;
        tbl.style.zoom = z;
      }
      setFitZoom(z);
      tbl.style.zoom = zoomMode === "fit" ? z : manualZoom;
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, [sorted, loading, zoomMode, manualZoom]);
  const stepZoom = (d) => { setZoomMode("manual"); setManualZoom(Math.min(1.5, Math.max(0.55, Math.round((tableZoom + d) * 20) / 20))); };

  const hasFilters = !!q.trim() || fCustomer !== "all" || fProduct !== "all" || fOps !== "all" || fPriority !== "all" || fPickup !== "all" || !!fDueBy || fRange !== "all" || fPaperwork !== "all" || fShipment !== "all";
  const clearFilters = () => { setQ(""); setFCustomer("all"); setFProduct("all"); setFOps("all"); setFPriority("all"); setFPickup("all"); setFDueBy(""); setFRange("all"); setFPaperwork("all"); setFShipment("all"); };
  const sortBtn = (key, label) => (
    <button type="button" onClick={() => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }))}
      className={`inline-flex items-center gap-1 hover:text-[#002FA7] ${sort.key === key ? "text-[#002FA7]" : ""}`} data-testid={`so-sort-${key}`}>
      {label} <ArrowUpDown className="h-3 w-3" />
    </button>
  );

  const TABS = [
    ["all", "All", "all"], ["open", "Open", "open"], ["ready", "Ready", "ready"],
    ["shipped", "Shipped", "shipped"], ["cancelled", "Cancelled", "cancelled"],
  ];
  const RISK_TABS = [
    ["all", "All", null], ["on_time", "On Time", "onTime"], ["at_risk", "At Risk", "atRisk"], ["past_due", "Past Due", "pastDue"],
  ];

  const EXPORT_HEADERS = ["Customer", "Item - Design", "DEL / PU", "Date of PO", "RDD", "PUD", "DD", "Customer PO#", "Pallet Qty", "Case Qty", "Bottles", "Case Pack Size", "Case Price", "Sales Value", "Shipment Status"];
  const [exportPicker, setExportPicker] = useState(null); // { grouped } | null
  const loadSavedCols = () => {
    try {
      const raw = JSON.parse(localStorage.getItem("so-export-excluded-cols") || "[]");
      return Array.isArray(raw) ? raw.filter((i) => Number.isInteger(i) && i >= 0 && i < 15) : [];
    } catch { return []; }
  };
  const [excludedCols, setExcludedCols] = useState([]);

  const exportTableExcel = (grouped = true, excluded = []) => {
    const isCaseUnit = (u) => !u || u === "case";
    const HEADERS = EXPORT_HEADERS;
    const inc = HEADERS.map((_, i) => !excluded.includes(i));
    const keep = (arr) => arr.filter((_, i) => inc[i]);
    const effDate = (so) => so.delivery_date || so.ship_date || "";
    const mondayIso = (iso) => {
      const d = new Date(`${iso}T00:00:00`);
      d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
      return d.toISOString().slice(0, 10);
    };
    const addD = (iso, n) => { const d = new Date(`${iso}T00:00:00`); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
    const thisMon = mondayIso(today());
    const mdOf = (iso) => { if (!iso) return ""; const [, m, d] = iso.split("-"); return `${Number(m)}/${Number(d)}`; };
    const mdyOf = (iso) => { if (!iso) return ""; const [y, m, d] = iso.split("-"); return `${Number(m)}/${Number(d)}/${y}`; };
    const df = grouped ? mdOf : mdyOf;
    const abbrevStatus = (s) => String(s || "").replace(/Picked Up/gi, "PU").replace(/Delivered/gi, "DEL");

    const soLineRows = (so) => (so.lines || []).map((l) => {
      const casePrice = l.price > 0 && isCaseUnit(l.price_unit) ? l.price
        : (so.price > 0 && isCaseUnit(so.price_unit) ? so.price : null);
      let value = linePriceTotal(l, products);
      if (value == null && so.price > 0) {
        const total = soPriceTotal(so, products);
        const totCases = so.lines.reduce((s, x) => s + (x.cases || 0), 0);
        if (total != null && totCases > 0) value = total * ((l.cases || 0) / totCases);
      }
      const { pallets } = lineLogistics(l, products);
      const isPickup = (so.pickup_delivery || "") === "pickup";
      const dos = so._dv?.dos || [];
      const shipStatus = dos.length
        ? [...new Set(dos.map((d) => abbrevStatus(doStatusMeta(d.status, isPickup).label)))].join(", ")
        : (so.status === "cancelled" ? "" : abbrevStatus(doStatusMeta("open", isPickup).label));
      return [
        so.customer_name || "",
        [l.product_name, l.design_name].filter(Boolean).join(" - "),
        isPickup ? "PU" : (so.pickup_delivery === "delivery" ? "DEL" : ""),
        df(so.order_date || ""), df(so.ship_date || ""), df(so.pickup_date || ""), df(so.delivery_date || ""),
        so.customer_po || "",
        pallets != null ? Math.round(pallets * 100) / 100 : "",
        l.cases || 0,
        Math.round((l.cases || 0) * (l.case_pack || 0)),
        l.case_pack ? Math.round(l.case_pack) : "",
        casePrice != null ? Math.round(casePrice * 100) / 100 : "",
        value != null ? Math.round(value * 100) / 100 : "",
        shipStatus,
      ];
    });

    const groups = new Map();
    for (const so of sorted) {
      const d = effDate(so);
      const key = d ? mondayIso(d) : "zz-none";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(so);
    }
    const keys = [...groups.keys()].sort();
    if (!keys.length) { toast.error("No orders to export"); return; }

    const thin = { style: "thin", color: { rgb: "000000" } };
    const BORDER = { border: { top: thin, bottom: thin, left: thin, right: thin } };
    const titleStyle = { font: { bold: true, sz: 11, color: { rgb: "9C0006" } }, fill: { patternType: "solid", fgColor: { rgb: "FFFF00" } }, ...BORDER };
    const totStyle = { font: { bold: true }, fill: { patternType: "solid", fgColor: { rgb: "FFC000" } }, ...BORDER };
    const headerCells = keep(HEADERS).map((h) => ({ v: h, t: "s", s: { font: { bold: true }, ...BORDER } }));
    const keptCount = headerCells.length;
    if (!keptCount) { toast.error("Select at least one column"); return; }
    const isShipped = (so) => {
      const dos = so._dv?.dos || [];
      return so.status === "shipped" || (dos.length > 0 && dos.every((x) => ["dispatched", "completed"].includes(x.status)));
    };
    const rowFill = (shipped) => ({ fill: { patternType: "solid", fgColor: { rgb: shipped ? "D8E4BC" : "F2DCDB" } }, ...BORDER });
    const aoa = [];
    const merges = [];
    if (!grouped) {
      aoa.push(headerCells);
      const flat = [...sorted].sort((a, b) => (effDate(a) || "9999").localeCompare(effDate(b) || "9999"));
      for (const so of flat) for (const row of soLineRows(so)) aoa.push(keep(row).map((v) => ({ v, t: typeof v === "number" ? "n" : "s", s: BORDER })));
    } else {
    for (const key of keys) {
      const sos = groups.get(key).sort((a, b) => (effDate(a) || "9999").localeCompare(effDate(b) || "9999"));
      let label = "Shipments with no ship date:";
      if (key !== "zz-none") {
        const end = addD(key, 4);
        const y1 = key.slice(0, 4), y2 = end.slice(0, 4);
        const range = y1 === y2
          ? `(${mdOf(key)} thru ${mdOf(end)}, ${y1})`
          : `(${mdOf(key)}, ${y1} thru ${mdOf(end)}, ${y2})`;
        if (key === thisMon) label = `Shipments This Week ${range}:`;
        else if (key === mondayIso(addD(thisMon, 7))) label = `Shipments next week ${range}:`;
        else if (key === mondayIso(addD(thisMon, 14))) label = `Shipments the following week ${range}:`;
        else label = `Shipments week of ${range}:`;
      }
      merges.push({ s: { r: aoa.length, c: 0 }, e: { r: aoa.length, c: keptCount - 1 } });
      aoa.push([{ v: label, t: "s", s: titleStyle }, ...Array(keptCount - 1).fill({ v: "", t: "s", s: titleStyle })]);
      aoa.push(headerCells);
      let tPal = 0, tCases = 0, tBottles = 0, tValue = 0;
      for (const so of sos) {
        const fill = rowFill(isShipped(so));
        for (const row of soLineRows(so)) {
          tPal += Number(row[8]) || 0; tCases += Number(row[9]) || 0; tBottles += Number(row[10]) || 0; tValue += Number(row[13]) || 0;
          aoa.push(keep(row).map((v) => ({ v, t: typeof v === "number" ? "n" : "s", s: fill })));
        }
      }
      const sub = Array(HEADERS.length).fill("");
      sub[0] = "TOTALS"; sub[8] = Math.round(tPal * 100) / 100; sub[9] = Math.round(tCases * 100) / 100;
      sub[10] = Math.round(tBottles); sub[13] = Math.round(tValue * 100) / 100;
      aoa.push(keep(sub).map((v) => ({ v, t: typeof v === "number" ? "n" : "s", s: totStyle })));
      aoa.push([]);
      aoa.push([]);
    }
    }
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!merges"] = merges;
    ws["!cols"] = keep([{ wch: 28 }, { wch: 36 }, { wch: 8 }, { wch: 10 }, { wch: 8 }, { wch: 8 }, { wch: 8 }, { wch: 17 }, { wch: 10 }, { wch: 9 }, { wch: 10 }, { wch: 13 }, { wch: 10 }, { wch: 12 }, { wch: 20 }]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sales Orders");
    XLSX.writeFile(wb, `sales-orders-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const printTable = async () => {
    const company = await getCompany();
    const rows = sorted.map((so) => ({
      customer: so.customer_name,
      so_number: so.so_number,
      po: so.customer_po,
      po_date: so.order_date,
      rdd: so.ship_date,
      pud: so.pickup_date || "",
      dd: so.delivery_date || "",
      pickup: so.pickup_delivery === "pickup" ? "PU" : (so.pickup_delivery === "delivery" ? "DEL" : ""),
      lines: (so.lines || []).map((l) => ({
        name: l.product_name, design: l.design_name || "",
        pack: l.case_pack ? `${Math.round(l.case_pack)}-Pack` : "", cases: l.cases || 0,
      })),
      total_cases: so.total_cases,
      price: soPriceTotal(so, products),
      status: so._ops?.label || "",
      ship_from: so.ship_from || "",
      ship_to: so.ship_to || "",
      bols: so._dv?.dos?.length || 0,
      priority: PRIORITY_META[so.priority]?.short || "",
    }));
    printSalesOrdersTable(rows, {
      companyName: company.company_name || "",
      tabLabel: TABS.find(([k]) => k === tab)?.[1] || "",
      filtered: hasFilters,
      totalCases: rows.reduce((s, r) => s + (r.total_cases || 0), 0),
      totalRevenue: rows.reduce((s, r) => s + (r.price || 0), 0),
    });
  };

  const copyRow = (so) => {
    const mdy = (iso) => { if (!iso) return ""; const [y, m, d] = iso.split("-"); return `${Number(m)}/${Number(d)}/${y}`; };
    const n2 = (v) => (Math.round(v * 100) / 100).toFixed(2);
    const clean = (v) => String(v ?? "").replace(/[\t\r\n]+/g, " ").trim();
    const isCaseUnit = (u) => !u || u === "case";
    const isPickup = (so.pickup_delivery || "") === "pickup";
    const delPuDate = isPickup ? (so.pickup_date || so.ship_date || "") : (so.delivery_date || so.ship_date || "");
    const status = (SO_STATUS_META[so.status] || SO_STATUS_META.open).label;
    const rows = (so.lines || []).map((l) => {
      const casePrice = l.price > 0 && isCaseUnit(l.price_unit) ? l.price
        : (so.price > 0 && isCaseUnit(so.price_unit) ? so.price : null);
      let value = linePriceTotal(l, products);
      if (value == null && so.price > 0) {
        const total = soPriceTotal(so, products);
        const totCases = so.lines.reduce((s, x) => s + (x.cases || 0), 0);
        if (total != null && totCases > 0) value = total * ((l.cases || 0) / totCases);
      }
      const prod = products.find((p) => p.id === l.product_id);
      return [
        clean(so.customer_name),
        so.pickup_delivery ? so.pickup_delivery.charAt(0).toUpperCase() + so.pickup_delivery.slice(1) : "",
        mdy(delPuDate),
        mdy(so.order_date || ""),
        clean(so.customer_po),
        clean([l.product_name || prod?.name, l.design_name].filter(Boolean).join(" - ")),
        Math.round(l.cases || 0),
        Math.round((l.cases || 0) * (l.case_pack || 0)),
        l.case_pack ? Math.round(l.case_pack) : "",
        casePrice != null ? n2(casePrice) : "",
        value != null ? n2(value) : "",
        status,
      ].join("\t");
    });
    navigator.clipboard.writeText(rows.join("\n"))
      .then(() => toast.success(`${so.so_number} copied — paste straight into your sheet (${rows.length} row${rows.length !== 1 ? "s" : ""})`))
      .catch(() => toast.error("Could not copy to clipboard"));
  };

  const rowActions = (so) => (
    <div className="flex items-center justify-end gap-1">
      <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-[#002FA7]" title="Copy row for spreadsheet — Customer, DEL/PU, dates, PO#, product, quantities, price, value, status" onClick={() => copyRow(so)} data-testid={`so-copy-${so.id}`}><Copy className="h-4 w-4" /></Button>
      <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-[#002FA7]" title="Edit order" onClick={() => setDialog({ open: true, initial: so })} data-testid={`so-edit-${so.id}`}><Pencil className="h-4 w-4" /></Button>
      <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-[#002FA7]" title="Print order confirmation" onClick={() => printSO(so)} data-testid={`so-print-${so.id}`}><Printer className="h-4 w-4" /></Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-[#002FA7]" data-testid={`so-more-${so.id}`}><MoreHorizontal className="h-4 w-4" /></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="rounded-sm">
          <DropdownMenuItem onClick={() => setDoFromSo(so)} data-testid={`so-create-do-${so.id}`}><Truck className="mr-2 h-3.5 w-3.5" /> Create Delivery Order</DropdownMenuItem>
          <DropdownMenuSeparator />
          {so.status === "open" && (
            <>
              <DropdownMenuItem onClick={() => changeStatus(so, "shipped")} data-testid={`so-mark-shipped-${so.id}`}><Truck className="mr-2 h-3.5 w-3.5" /> Mark Shipped</DropdownMenuItem>
              <DropdownMenuItem onClick={() => changeStatus(so, "cancelled")} data-testid={`so-mark-cancelled-${so.id}`}><X className="mr-2 h-3.5 w-3.5" /> Cancel Order</DropdownMenuItem>
            </>
          )}
          {so.status !== "open" && (
            <DropdownMenuItem onClick={() => changeStatus(so, "open")} data-testid={`so-reopen-${so.id}`}><RotateCcw className="mr-2 h-3.5 w-3.5" /> Reopen</DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-red-600 focus:text-red-600" onClick={() => setDelSo(so)} data-testid={`so-delete-${so.id}`}><Trash2 className="mr-2 h-3.5 w-3.5" /> Delete</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  return (
    <main className="mx-auto max-w-full px-4 py-8 sm:px-6 md:px-8 md:py-10" data-testid="sales-orders-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Operations</p>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tighter text-slate-900 sm:text-5xl">Sales Orders</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setPoDragOver(true); }}
            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setPoDragOver(true); }}
            onDragLeave={(e) => { e.preventDefault(); setPoDragOver(false); }}
            onDrop={(e) => {
              e.preventDefault(); e.stopPropagation(); setPoDragOver(false);
              if (poCreating) return;
              const f = e.dataTransfer?.files?.[0];
              if (f) createFromPo(f);
              else toast.error("Drop a file — dragging directly from some email previews isn't supported; save the attachment first");
            }}
            className={`inline-flex cursor-pointer items-center rounded-sm border px-4 py-2 text-sm font-semibold transition-colors ${poDragOver ? "border-dashed border-[#002FA7] bg-blue-50 text-[#002FA7]" : "border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/5"} ${poCreating ? "pointer-events-none opacity-60" : ""}`}
            data-testid="new-so-from-po-button" title="Upload or drag a customer PO here — a Sales Order will be created from it automatically">
            {poCreating ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4 text-amber-500" />}
            {poCreating ? "Reading PO…" : poDragOver ? "Drop PO here" : "New Order from PO"}
            <input type="file" accept=".pdf,.xlsx,.png,.jpg,.jpeg,.txt,.csv" className="hidden" disabled={poCreating}
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) createFromPo(f); }} />
          </label>
          <Button onClick={() => setDialog({ open: true, initial: null })} className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="new-so-button">
            <Plus className="mr-1.5 h-4 w-4" /> New Sales Order
          </Button>
        </div>
      </div>

      {/* Tab groups + search */}
      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="inline-flex flex-wrap rounded-sm border border-slate-200 bg-slate-50 p-1" data-testid="so-tabs">
          {TABS.map(([k, lbl, ck]) => (
            <button key={k} type="button" onClick={() => setTab(k)}
              className={`rounded-sm px-4 py-1.5 text-sm font-semibold transition-colors ${tab === k ? "bg-white text-[#002FA7] shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
              data-testid={`so-tab-${k}`}>
              {lbl} <span className="ml-1 text-xs font-normal text-slate-400">{counts[ck]}</span>
            </button>
          ))}
        </div>
        <div className="inline-flex rounded-sm border border-slate-200 bg-slate-50 p-1" data-testid="so-risk-tabs">
          {RISK_TABS.map(([k, lbl, ck]) => (
            <button key={k} type="button" onClick={() => setFRisk(k)}
              className={`rounded-sm px-3 py-1.5 text-xs font-semibold transition-colors ${fRisk === k ? "bg-white text-[#002FA7] shadow-sm" : "text-slate-500 hover:text-slate-800"}`}
              data-testid={`so-risk-${k}`}>
              {lbl}{ck ? <span className="ml-1 text-xs font-normal text-slate-400">{counts[ck]}</span> : null}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search Sales Orders"
            className="h-9 w-52 rounded-sm border-slate-200 pl-8 pr-8 text-sm focus-visible:ring-2 focus-visible:ring-[#002FA7]" data-testid="so-search" />
          {q && (
            <button type="button" onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" data-testid="so-search-clear">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Secondary filters */}
      <div className="mb-5 mt-3 flex flex-wrap items-center gap-2">
        <Select value={fCustomer} onValueChange={setFCustomer}>
          <SelectTrigger className="h-9 w-[150px] rounded-sm text-xs" data-testid="so-filter-customer"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All customers</SelectItem>
            {customers.map((c) => (<SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={fProduct} onValueChange={setFProduct}>
          <SelectTrigger className="h-9 w-[150px] rounded-sm text-xs" data-testid="so-filter-product"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All products</SelectItem>
            {products.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={fOps} onValueChange={setFOps}>
          <SelectTrigger className="h-9 w-[170px] rounded-sm text-xs" data-testid="so-filter-ops"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All operational statuses</SelectItem>
            {Object.entries(OPS_META).map(([k, m]) => (<SelectItem key={k} value={k}>{m.label}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={fPriority} onValueChange={setFPriority}>
          <SelectTrigger className="h-9 w-[130px] rounded-sm text-xs" data-testid="so-filter-priority"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            {[1, 2, 3, 4].map((k) => (<SelectItem key={k} value={String(k)}>{PRIORITY_META[k].label}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={fPickup} onValueChange={setFPickup}>
          <SelectTrigger className="h-9 w-[150px] rounded-sm text-xs" data-testid="so-filter-pickup"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Pickup &amp; Delivery</SelectItem>
            <SelectItem value="pickup">Pickup only</SelectItem>
            <SelectItem value="delivery">Delivery only</SelectItem>
            <SelectItem value="unset">Not set</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fRange} onValueChange={setFRange}>
          <SelectTrigger className={`h-9 w-[140px] rounded-sm text-xs ${fRange !== "all" ? "border-[#002FA7] text-[#002FA7]" : ""}`} data-testid="so-filter-range"><SelectValue /></SelectTrigger>
          <SelectContent>
            {RANGE_OPTIONS.map(([k, lbl]) => (<SelectItem key={k} value={k}>{lbl}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={fPaperwork} onValueChange={setFPaperwork}>
          <SelectTrigger className={`h-9 w-[170px] rounded-sm text-xs ${fPaperwork !== "all" ? "border-[#002FA7] text-[#002FA7]" : ""}`} data-testid="so-filter-paperwork"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PAPERWORK_OPTIONS.map(([k, lbl]) => (<SelectItem key={k} value={k}>{lbl}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={fShipment} onValueChange={setFShipment}>
          <SelectTrigger className={`h-9 w-[160px] rounded-sm text-xs ${fShipment !== "all" ? "border-[#002FA7] text-[#002FA7]" : ""}`} data-testid="so-filter-shipment"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Shipment: All</SelectItem>
            {Object.entries(DO_STATUS_META).filter(([k]) => k !== "cancelled").map(([k, m]) => (
              <SelectItem key={k} value={k} data-testid={`so-filter-shipment-${k}`}>
                {m.pickupLabel && m.pickupLabel !== m.label ? `${m.label} / ${m.pickupLabel}` : m.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Due by</span>
          <Input type="date" value={fDueBy} onChange={(e) => setFDueBy(e.target.value)} className="h-9 w-[140px] rounded-sm text-xs" data-testid="so-filter-due-by" />
          {fDueBy && <button type="button" onClick={() => setFDueBy("")} className="text-slate-400 hover:text-slate-700" data-testid="so-filter-due-clear"><X className="h-3.5 w-3.5" /></button>}
        </div>
        {hasFilters && (
          <button type="button" onClick={clearFilters} className="inline-flex items-center gap-1 text-xs font-semibold text-[#002FA7] hover:underline" data-testid="so-clear-filters">
            <X className="h-3 w-3" /> Clear Filters
          </button>
        )}
        <div className="ml-auto flex items-center gap-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          <div className="hidden items-center gap-1 md:flex" data-testid="so-zoom-controls">
            <button type="button" onClick={printTable} title="Print this table" className="p-1 hover:text-[#002FA7]" data-testid="so-print-table"><Printer className="h-3.5 w-3.5" /></button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" title="Download this table as Excel" className="mr-1 p-1 hover:text-[#002FA7]" data-testid="so-export-excel"><Download className="h-3.5 w-3.5" /></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-sm">
                <DropdownMenuItem onClick={() => { setExcludedCols(loadSavedCols()); setExportPicker({ grouped: true }); }} data-testid="so-export-weekly">Excel — grouped week by week</DropdownMenuItem>
                <DropdownMenuItem onClick={() => { setExcludedCols(loadSavedCols()); setExportPicker({ grouped: false }); }} data-testid="so-export-flat">Excel — all in one list</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <button type="button" onClick={() => stepZoom(-0.05)} title="Zoom out" className="p-1 hover:text-[#002FA7]" data-testid="so-zoom-out"><ZoomOut className="h-3.5 w-3.5" /></button>
            <span className="w-9 text-center tabular-nums" data-testid="so-zoom-level">{Math.round(tableZoom * 100)}%</span>
            <button type="button" onClick={() => stepZoom(0.05)} title="Zoom in" className="p-1 hover:text-[#002FA7]" data-testid="so-zoom-in"><ZoomIn className="h-3.5 w-3.5" /></button>
            {zoomMode !== "fit" && (
              <button type="button" onClick={() => setZoomMode("fit")} className="ml-0.5 font-semibold text-[#002FA7] hover:underline" data-testid="so-zoom-fit">Fit</button>
            )}
          </div>
          <span>Sort:</span>
          {sortBtn("po_date", "Date of PO")}
          {sortBtn("date", "RDD")}
          {sortBtn("priority", "Priority")}
        </div>
      </div>

      {!loading && revenue.count > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-1 border border-slate-200 bg-slate-50 px-4 py-2.5" data-testid="so-revenue-summary">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{hasFilters || tab !== "all" ? "Filtered orders" : "All orders"}</span>
          <span className="font-heading text-lg font-black tabular-nums tracking-tight text-emerald-700" data-testid="so-revenue-total">{money(revenue.total)}</span>
          <span className="text-xs text-slate-500">
            combined sales value · {revenue.priced} of {revenue.count} order{revenue.count !== 1 ? "s" : ""} priced
            {revenue.priced < revenue.count && <span className="text-slate-400"> ({revenue.count - revenue.priced} without a price)</span>}
          </span>
          <span className="text-xs tabular-nums text-slate-500">{num(revenue.cases)} cases</span>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
      ) : sorted.length === 0 ? (
        <div className="mt-6 flex flex-col items-center border border-dashed border-slate-300 px-6 py-16 text-center" data-testid="so-empty-state">
          <ShoppingCart className="h-10 w-10 text-slate-300" strokeWidth={1.5} />
          <h2 className="mt-4 font-heading text-xl font-bold tracking-tight text-slate-900">No sales orders here.</h2>
          <p className="mt-2 max-w-md text-sm text-slate-500">Create a sales order to reserve finished goods for a customer — reservations appear instantly on the Finished Goods inventory.</p>
        </div>
      ) : (
        <>
        <div ref={tableWrapRef} className="hidden max-h-[calc(100vh-140px)] overflow-auto border border-slate-200 md:block" data-testid="so-table">
          <table ref={tableRef} style={{ zoom: tableZoom }} className="w-full border-collapse text-sm">
            <thead className="sticky top-0 z-10 bg-slate-50 shadow-[0_1px_0_0_#e2e8f0]">
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-[0.08em] text-slate-600">
                <th className="px-3 py-3">Customer</th>
                <th className="border-l border-slate-200 px-3 py-3" data-testid="so-th-dates">
                  <div>Dates</div>
                  <div className="mt-1 flex items-center gap-2">
                    <button type="button" className="inline-flex cursor-pointer items-center gap-0.5 hover:text-[#002FA7]" onClick={() => setSort((s) => ({ key: "po_date", dir: s.key === "po_date" && s.dir === "desc" ? "asc" : "desc" }))} data-testid="so-th-po-date">
                      <span className={`inline-flex items-center gap-0.5 text-[10px] ${sort.key === "po_date" ? "text-[#002FA7]" : "text-slate-400"}`}>PO <ArrowUpDown className="h-2.5 w-2.5" /></span>
                    </button>
                    <button type="button" className="inline-flex cursor-pointer items-center gap-0.5 hover:text-[#002FA7]" onClick={() => setSort((s) => ({ key: "date", dir: s.key === "date" && s.dir === "asc" ? "desc" : "asc" }))} data-testid="so-th-rdd">
                      <span className={`inline-flex items-center gap-0.5 text-[10px] ${sort.key === "date" ? "text-[#002FA7]" : "text-slate-400"}`}>RDD <ArrowUpDown className="h-2.5 w-2.5" /></span>
                    </button>
                    <button type="button" className="inline-flex cursor-pointer items-center gap-0.5 hover:text-[#002FA7]" onClick={() => setSort((s) => ({ key: "dd", dir: s.key === "dd" && s.dir === "asc" ? "desc" : "asc" }))} data-testid="so-th-dd">
                      <span className={`inline-flex items-center gap-0.5 text-[10px] ${sort.key === "dd" ? "text-[#002FA7]" : "text-slate-400"}`}>DD <ArrowUpDown className="h-2.5 w-2.5" /></span>
                    </button>
                  </div>
                </th>
                <th className="border-l border-slate-200 px-3 py-3">Customer PO #</th>
                <th className="border-l border-slate-200 px-3 py-3">Order</th>
                <th className="border-l border-slate-200 px-3 py-3">Price</th>
                <th className="border-l border-slate-200 px-3 py-3">Fulfillment</th>
                <th className="border-l border-slate-200 px-3 py-3">DEL / PU</th>
                <th className="border-l border-slate-200 px-3 py-3">Origin</th>
                <th className="border-l border-slate-200 px-3 py-3 min-w-[150px]">Destination</th>
                <th className="border-l border-slate-200 px-3 py-3">Generate BOL(s)</th>
                <th className="border-l border-slate-200 px-3 py-3">Carrier</th>
                <th className="border-l border-slate-200 px-3 py-3">Load Photos</th>
                <th className="border-l border-slate-200 px-3 py-3">Shipment Status</th>
                <th className="border-l border-slate-200 px-3 py-3">Customer Invoices</th>
                <th className="border-l border-slate-200 px-3 py-3">SO #</th>
                <th className="border-l border-slate-200 px-3 py-3">Priority</th>
                <th className="w-28 border-l border-slate-200 px-3 py-3" />
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((so) => {
                const dim = so.status !== "open" ? "opacity-70" : "";
                const dupPo = so.status !== "cancelled" && dupPoSet.has((so.customer_po || "").trim());
                return (
                  <tr key={so.id} onClick={() => setViewSoId(so.id)}
                    title={dupPo ? `PO ${so.customer_po} appears on more than one order — possible duplicate` : undefined}
                    className={`cursor-pointer border-b border-slate-100 ${dupPo ? "bg-red-50 hover:bg-red-100/60" : "hover:bg-slate-50/60"} ${dim}`} data-testid={`so-row-${so.id}`}>
                    <td className="px-3 py-3 font-medium text-slate-800">{so.customer_name}</td>
                    <td className="border-l border-slate-100 px-3 py-3 text-slate-600" data-testid={`so-dates-${so.id}`}>
                      <div className="space-y-1 whitespace-nowrap">
                        <div className="flex items-center gap-1.5"><span className="w-8 shrink-0 text-[10px] font-bold uppercase text-slate-400">PO</span><PoDateCell so={so} onSaved={load} /></div>
                        <div className="flex items-center gap-1.5"><span className="w-8 shrink-0 text-[10px] font-bold uppercase text-slate-400">RDD</span><DateCell so={so} onSaved={load} /></div>
                        <div className="flex items-center gap-1.5"><span className="w-8 shrink-0 text-[10px] font-bold uppercase text-slate-400">PUD</span><PickupDateCell so={so} onSaved={load} /><TimeCell so={so} field="pickup_time" endpoint="pickup-time" label="pickup time" onSaved={load} /></div>
                        <div className="flex items-center gap-1.5"><span className="w-8 shrink-0 text-[10px] font-bold uppercase text-slate-400">DD</span><DeliveryDateCell so={so} onSaved={load} /><TimeCell so={so} field="delivery_time" endpoint="delivery-time" label="delivery time" onSaved={load} /></div>
                      </div>
                    </td>
                    <td className="border-l border-slate-100 px-3 py-3 tabular-nums text-slate-800" data-testid={`so-po-${so.id}`}>
                      <div className="font-medium">{so.customer_po || "—"}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-2">
                        <SoPoStatusBadge so={so} onChanged={load} />
                        <SoPoAttach so={so} onChanged={load} />
                      </div>
                    </td>
                    <td className="border-l border-slate-100 px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <SoOrderCell so={so} products={products} onChanged={load} />
                    </td>
                    <td className="border-l border-slate-100 px-3 py-3" data-testid={`so-price-${so.id}`} onClick={(e) => e.stopPropagation()}>
                      <SoPriceCell so={so} products={products} onSaved={load} />
                    </td>
                    <td className="border-l border-slate-100 px-3 py-3"><FulfillmentSummary so={so} onSchedule={() => scheduleShort(so)} scheduling={schedulingSo === so.id} onProduce={setProduceMo} onAddStock={setAddStockTarget} onRecordProduction={recordProduction} onUnproduce={unproduceMo} onChanged={load} /></td>
                    <td className="border-l border-slate-100 px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <SoPickupDelivery so={so} onChanged={load} />
                    </td>
                    <td className="border-l border-slate-100 px-3 py-3" data-testid={`so-ship-from-cell-${so.id}`} onClick={(e) => e.stopPropagation()}>
                      <SoShipFrom so={so} options={shipFromOpts} onChanged={load} />
                    </td>
                    <td className="border-l border-slate-100 px-3 py-3" data-testid={`so-ship-to-${so.id}`} onClick={(e) => e.stopPropagation()}>
                      <SoShipToCell so={so} onSaved={load} />
                    </td>
                    <td className="border-l border-slate-100 px-3 py-3" data-testid={`so-bol-cell-${so.id}`} onClick={(e) => e.stopPropagation()}>
                      <SoBolCell so={so} customers={customers} getCompany={getCompany} onGenerate={() => setBolSo(so)} onChanged={load} onEditBol={setEditBol} />
                    </td>
                    <td className="border-l border-slate-100 px-3 py-3" data-testid={`so-carrier-cell-${so.id}`} onClick={(e) => e.stopPropagation()}>
                      <SoCarrierCell so={so} carriers={carriers} onChanged={load} shopCtx={{ getCompany, landFreight, customers, products }} />
                    </td>
                    <td className="border-l border-slate-100 px-3 py-3" data-testid={`so-load-photos-cell-${so.id}`} onClick={(e) => e.stopPropagation()}>
                      <SoLoadPhotosCell so={so} onChanged={load} />
                    </td>
                    <td className="border-l border-slate-100 px-3 py-3" data-testid={`so-shipment-status-td-${so.id}`} onClick={(e) => e.stopPropagation()}>
                      <SoShipmentStatusCell so={so} onChanged={load} />
                    </td>
                    <td className="border-l border-slate-100 px-3 py-3" data-testid={`so-invoice-cell-${so.id}`} onClick={(e) => e.stopPropagation()}>
                      <SoInvoiceCell so={so} customers={customers} getCompany={getCompany} onGenerate={() => setInvSo(so)} onChanged={load} />
                    </td>
                    <td className="border-l border-slate-100 px-3 py-3">
                      <button className="font-semibold text-[#002FA7] hover:underline" onClick={(e) => { e.stopPropagation(); setViewSoId(so.id); }} data-testid={`so-number-${so.id}`}>{so.so_number}</button>
                      <div className="mt-0.5 text-xs text-slate-400">
                        {so.lines.length} SKU{so.lines.length !== 1 ? "s" : ""}
                      </div>
                    </td>
                    <td className="border-l border-slate-100 px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      <PriorityCell so={so} onChanged={setPriorityLocal} />
                    </td>
                    <td className="border-l border-slate-100 px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      {rowActions(so)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {showMoreBar && <div className="hidden md:block">{showMoreBar}</div>}

        <div className="mt-4 space-y-3 md:hidden" data-testid="so-cards">
          {visibleRows.map((so) => {
            const dupPo = so.status !== "cancelled" && dupPoSet.has((so.customer_po || "").trim());
            return (
              <div key={so.id} onClick={() => setViewSoId(so.id)}
                className={`border p-4 ${dupPo ? "border-red-200 bg-red-50" : "border-slate-200 bg-white"} ${so.status !== "open" ? "opacity-70" : ""}`}
                data-testid={`so-card-${so.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-slate-900">{so.customer_name}</p>
                    <p className="text-sm">
                      <span className="font-semibold text-[#002FA7]">{so.so_number}</span>
                      <span className="ml-2 text-xs text-slate-400">{so.lines.length} SKU{so.lines.length !== 1 ? "s" : ""}</span>
                    </p>
                    {dupPo && <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-600">Possible duplicate PO</p>}
                  </div>
                  <div className="flex shrink-0 items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <PriorityCell so={so} onChanged={setPriorityLocal} />
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 text-sm" onClick={(e) => e.stopPropagation()}>
                  <MobileField label="RDD"><DateCell so={so} onSaved={load} /></MobileField>
                  <MobileField label="Date of PO"><PoDateCell so={so} onSaved={load} /></MobileField>
                  <MobileField label="Pickup Date"><div className="flex items-center gap-2"><PickupDateCell so={so} onSaved={load} /><TimeCell so={so} field="pickup_time" endpoint="pickup-time" label="pickup time" onSaved={load} /></div></MobileField>
                  <MobileField label="Delivery Date"><div className="flex items-center gap-2"><DeliveryDateCell so={so} onSaved={load} /><TimeCell so={so} field="delivery_time" endpoint="delivery-time" label="delivery time" onSaved={load} /></div></MobileField>
                  <MobileField label="Load Photos"><SoLoadPhotosCell so={so} onChanged={load} /></MobileField>
                  <MobileField label="Customer PO #">
                    <div className="font-medium tabular-nums text-slate-800">{so.customer_po || "—"}</div>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      <SoPoStatusBadge so={so} onChanged={load} />
                      <SoPoAttach so={so} onChanged={load} />
                    </div>
                  </MobileField>
                  <MobileField label="DEL / PU"><SoPickupDelivery so={so} onChanged={load} /></MobileField>
                  <MobileField label="Order" full><SoOrderCell so={so} products={products} onChanged={load} /></MobileField>
                  <MobileField label="Price"><SoPriceCell so={so} products={products} onSaved={load} /></MobileField>
                  <MobileField label="Fulfillment"><FulfillmentSummary so={so} onSchedule={() => scheduleShort(so)} scheduling={schedulingSo === so.id} onProduce={setProduceMo} onAddStock={setAddStockTarget} onRecordProduction={recordProduction} onUnproduce={unproduceMo} onChanged={load} /></MobileField>
                  <MobileField label="Origin"><SoShipFrom so={so} options={shipFromOpts} onChanged={load} /></MobileField>
                  <MobileField label="Destination" full><SoShipToCell so={so} onSaved={load} /></MobileField>
                  <MobileField label="BOLs" full><SoBolCell so={so} customers={customers} getCompany={getCompany} onGenerate={() => setBolSo(so)} onChanged={load} onEditBol={setEditBol} /></MobileField>
                  <MobileField label="Carrier" full><SoCarrierCell so={so} carriers={carriers} onChanged={load} shopCtx={{ getCompany, landFreight, customers, products }} /></MobileField>
                  <MobileField label="Shipment Status" full><SoShipmentStatusCell so={so} onChanged={load} /></MobileField>
                  <MobileField label="Invoices" full><SoInvoiceCell so={so} customers={customers} getCompany={getCompany} onGenerate={() => setInvSo(so)} onChanged={load} /></MobileField>
                </div>

                <div className="mt-3 flex items-center justify-end border-t border-slate-100 pt-2" onClick={(e) => e.stopPropagation()}>
                  {rowActions(so)}
                </div>
              </div>
            );
          })}
          {showMoreBar}
        </div>
        </>
      )}

      <SODetailDialog
        so={enriched.find((s) => s.id === viewSoId) || null}
        products={products}
        onChanged={load}
        onOpenChange={(o) => { if (!o) setViewSoId(null); }}
        onEdit={() => { const s = enriched.find((x) => x.id === viewSoId); setViewSoId(null); if (s) setDialog({ open: true, initial: s }); }}
        onPrint={() => { const s = enriched.find((x) => x.id === viewSoId); if (s) printSO(s); }}
        onWaive={(line, waived) => {
          const s = enriched.find((x) => x.id === viewSoId);
          if (!s) return;
          if (waived) setWaiveTarget({ so: s, line });
          else doWaive(s, line, false);
        }}
      />

      <ProduceConfirmDialog mo={produceMo} onOpenChange={(o) => { if (!o) setProduceMo(null); }}
        onDone={() => { setProduceMo(null); load(); }} />
      <SoAddStockDialog target={addStockTarget} products={products} onOpenChange={(o) => { if (!o) setAddStockTarget(null); }}
        onDone={() => { setAddStockTarget(null); load(); }} />
      <CreateMoDialog so={createMoFor} products={products} busy={creatingMo} onClose={() => setCreateMoFor(null)} onCreate={confirmCreateMos} />
      <GenerateInvoiceDialog so={invSo} customers={customers} getCompany={getCompany}
        onOpenChange={(o) => { if (!o) setInvSo(null); }}
        onSaved={() => { setInvSo(null); load(); }} />
      <GenerateBolDialog so={bolSo} products={products} customers={customers}
        onOpenChange={(o) => { if (!o) setBolSo(null); }}
        onSaved={() => { setBolSo(null); load(); }} />
      <DODialog open={!!editBol} onOpenChange={(o) => { if (!o) setEditBol(null); }} existing={editBol} so={null} products={products} customers={customers}
        onSaved={() => { setEditBol(null); load(); }} />
      <DODialog open={!!doFromSo} onOpenChange={(o) => { if (!o) setDoFromSo(null); }} so={doFromSo} existing={null} products={products} customers={customers}
        onSaved={() => setDoFromSo(null)} />

      <SODialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} initial={dialog.initial} customers={customers} products={products} onSaved={() => load()} />

      <AlertDialog open={!!delSo} onOpenChange={(o) => { if (!o) setDelSo(null); }}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {delSo?.so_number}?</AlertDialogTitle>
            <AlertDialogDescription>This removes the sales order and releases its finished goods reservations.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="rounded-sm bg-red-600 text-white hover:bg-red-700" data-testid="so-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!waiveTarget} onOpenChange={(o) => { if (!o) setWaiveTarget(null); }}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Waive production for {waiveTarget?.line?.product_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              The {num(waiveTarget?.line?.shortage_cases || 0)}-case shortage will be marked as Waived (accepted, no production)
              {waiveTarget?.line?.mo_id ? ` and ${waiveTarget.line.mo_number || "the linked MO"} will be removed from the production queue, releasing its component reservations` : ""}.
              This line won't trigger auto-created MOs until you resume production.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { doWaive(waiveTarget.so, waiveTarget.line, true); setWaiveTarget(null); }}
              className="rounded-sm bg-slate-700 text-white hover:bg-slate-800" data-testid="so-waive-confirm">
              Waive Production
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!exportPicker} onOpenChange={(o) => { if (!o) setExportPicker(null); }}>
        <DialogContent className="max-w-md rounded-sm" data-testid="export-column-picker">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight">Export Columns</DialogTitle>
            <DialogDescription>
              All columns are included by default — untick any you want to leave out of the {exportPicker?.grouped ? "weekly" : "flat"} Excel export.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {EXPORT_HEADERS.map((h, i) => (
              <label key={h} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                <Checkbox checked={!excludedCols.includes(i)}
                  onCheckedChange={(c) => setExcludedCols((prev) => (c ? prev.filter((x) => x !== i) : [...prev, i]))}
                  data-testid={`export-col-${i}`} />
                {h}
              </label>
            ))}
          </div>
          <div className="flex items-center justify-between pt-1">
            <button type="button" onClick={() => setExcludedCols([])}
              className="text-xs font-semibold text-slate-500 underline-offset-2 hover:text-[#002FA7] hover:underline"
              data-testid="export-cols-all">Select all</button>
            <Button onClick={() => {
              try { localStorage.setItem("so-export-excluded-cols", JSON.stringify(excludedCols)); } catch { /* ignore */ }
              exportTableExcel(exportPicker.grouped, excludedCols);
              setExportPicker(null);
            }}
              className="rounded-sm bg-[#002FA7] text-white hover:bg-[#002080]" data-testid="export-confirm">
              <Download className="mr-1.5 h-4 w-4" /> Export {EXPORT_HEADERS.length - excludedCols.length} columns
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </main>
  );
}
